<script lang="ts" setup>
import {
  onMounted,
  onUnmounted,
  ref,
  computed,
  watch,
  watchEffect,
  markRaw,
  nextTick,
  provide,
} from "vue";
import {
  CONNECTION_CONTEXT_KEY,
  type BuilderConnectionContext,
} from "./composables/connection-context";
import { BBEL_SCOPE_KEY, SELF_SAMPLE_KEY, type BbelScopeContext, type SelfSampleContext } from "./composables/bbel-context";
import { buildWorkflowScope, useBbelScope } from "./composables/useBbelScope";
import { useConnectionTemplateDetails } from "@/queries/connection-templates";
import { useWorkflowOutputSamples } from "@/queries/output-samples";
import { useBbelFunctions } from "@/queries/expressions";
import type { ScopeDescriptor } from "@/types/bbel-types";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useSessionStore } from "@/stores/session-store";
import { useBuilderPrefsStore } from "@/stores/builder-prefs-store";
import { useProjects, useProjectWorkflows, useSetWorkflowActive } from "@/queries/projects";
import { useProducts } from "@/queries/products";
import { useWorkflow, useWorkflowRuns, useUpdateService, useSetServiceIsComponent, usePublishWorkflow, useDiscardWorkflowChanges, useWorkflowVersions, useRestoreWorkflowVersion, useImportWorkflow, type WorkflowExportBundle } from "@/queries/workflows";
import { useGetWorkflowRun } from "@/queries/runs";
import { useRunWithReadinessConfirm, RunCancelledError } from "@/composables/useRunWithReadinessConfirm";
import { useQueryClient } from "@tanstack/vue-query";
import { useAlertStore } from "@/stores/alerts-store";
import RunReadinessConfirmDialog from "@/components/RunReadinessConfirmDialog.vue";
import { VueFlow, useVueFlow } from "@vue-flow/core";
import { Background } from '@vue-flow/background'
import { useRecentlyViewed } from "@/composables/useRecentlyViewed";
import ServiceNode from "./components/ServiceNode.vue";
import ServiceDetailsPanel from "./components/ServiceDetailsPanel.vue";
import RunServiceDetailsPanel from "./components/RunServiceDetailsPanel.vue";
import RunCanvas from "@/views/runhistory/RunCanvas.vue";
import AddServicePanel from "./components/AddServicePanelV2.vue";
import AnimatedEdge from "./components/AnimatedEdge.vue";
import FilterWhenPopover from "./components/FilterWhenPopover.vue";
import RunServiceModal from "./components/RunServiceModal.vue";
import BuilderHeader from "./components/BuilderHeader.vue";
import HistoryPanel from "./components/HistoryPanel.vue";
import BuilderToolbar from "./components/BuilderToolbar.vue";
import WorkflowWizardBuilder from "@/views/shared/components/wizard/WorkflowWizardBuilder.vue";
import FormAlert from "@/components/FormAlert.vue";
import StackedWorkflowView from "./components/StackedWorkflowView.vue";
import EditParentServicesModal from "./components/EditParentServicesModal.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import RunSummaryHeader from "./components/RunSummaryHeader.vue";
import RunWorkflowModal from "@/views/projects/components/tabs/RunWorkflowModal.vue";
import { useWorkflowCanvas } from "./composables/useWorkflowCanvas";
import { useBuilderState } from "./composables/useBuilderState";
import { useNodeOperations } from "./composables/useNodeOperations";
import { useVersionManagement } from "./composables/useVersionManagement";
import { useWorkflowOperations } from "./composables/useWorkflowOperations";
import { useHistoryManagement } from "./composables/useHistoryManagement";
import { nodeBadges } from "./utils/nodeBadges";
import { edgeFilter } from "./utils/edgeFilter";
import { useRunSimulator } from "@/composables/useRunSimulator";
import { useDebugStore } from "@/stores/debug-store";
import { useHistoryFilters } from "./composables/useHistoryFilters";
import { useBuilderNavigation } from "./composables/useBuilderNavigation";
import { useApplyAndRun } from "./composables/useApplyAndRun";
import CustomizeWorkflowModal from "@/views/projects/components/CustomizeWorkflowModal.vue";
import ApplyAndRunConfirmModal from "./components/ApplyAndRunConfirmModal.vue";
import { useServiceManagement } from "./composables/useServiceManagement";
import { useRunProgress, applyProgress, applyServiceCounts } from "@/composables/useRunProgress";
import { edgeStyleForStatus } from "@/composables/edge-colors";
import { storedCanvasPosition } from "@/composables/canvas-layout";
import type { Paged } from "@/queries/projects";
import type { WorkflowEdge, WorkflowNode } from "./data/initialWorkflowData";
import type {RunHistory, Service} from "@/types";
import { serviceTypeDisplay, isFinishedRunStatus } from "@/types";

defineProps<{
  sideNav?: boolean;
}>();

defineEmits<{
  toggleFullscreen: [];
}>();

const route = useRoute();
const router = useRouter();
const sessionStore = useSessionStore();
const { session } = storeToRefs(sessionStore);
const builderPrefs = useBuilderPrefsStore();
const { projects } = useProjects();
const { products } = useProducts();
const { zoomIn, zoomOut, fitView, getViewport, setViewport } = useVueFlow();

const workflowId = computed(() => (route.params.workflowid as string) || "");
const projectId = computed(() => (route.params.projectid as string) || "");
const { workflow, workflowLoading } = useWorkflow(workflowId);

// ADR-030 — make the editing workflow's connection slots available to the service-model connection
// picker, without prop-drilling through ServiceEditor's dynamic <component>.
provide<BuilderConnectionContext>(CONNECTION_CONTEXT_KEY, {
  workflowId,
  connectionSlots: computed(() => workflow.value?.connectionSlots ?? []),
});

// #5683 — the BBEL editor's scope (services / wizard / connection slots / fixed engine scopes + the
// function catalogue), derived from the editing workflow and provided to the service-model expression
// fields (same no-prop-drill pattern as the connection context above). Output samples (this
// project's latest successful run) hang the real response shape under each service's `output`.
const { outputSamples, refetchOutputSamples } = useWorkflowOutputSamples(projectId, workflowId);
// Slot-referenced template details put each template's non-secret fields under connection.<slot>.
const slotTemplateIds = computed(() => [
  ...new Set((workflow.value?.connectionSlots ?? []).map((s) => s.connectionTemplateId).filter(Boolean)),
]);
const { templatesById: slotTemplates } = useConnectionTemplateDetails(slotTemplateIds);
// Builder UI state (selected service + modals). Hoisted above the BBEL scope so the editor and the
// binary picker can be scoped to the selected service's ancestors (upstream-only references).
const builderState = useBuilderState();
const bbelScopeRoots = useBbelScope(workflow, outputSamples, slotTemplates, builderState.selectedServiceId);
const { functions: bbelFunctions } = useBbelFunctions();
provide<BbelScopeContext>(
  BBEL_SCOPE_KEY,
  computed<ScopeDescriptor>(() => ({ roots: bbelScopeRoots.value, functions: bbelFunctions.value })),
);
// The selected service's own latest sample — the response-validation builder offers its real
// self.output.* shape as field suggestions (#6022).
provide<SelfSampleContext>(
  SELF_SAMPLE_KEY,
  computed(() => {
    const id = builderState.selectedServiceId.value;
    return id ? outputSamples.value[id] : undefined;
  }),
);
const historyFilters = useHistoryFilters({ scope: "workflow", id: workflowId });
const { workflowRuns, workflowRunsTotal, workflowRunsLoading, workflowRunsFetching } = useWorkflowRuns(projectId, workflowId, historyFilters.serverFilterParams);
const { updateServiceAsync, updateServiceError, updateServiceReset } = useUpdateService(workflowId);
const { setServiceIsComponentAsync } = useSetServiceIsComponent(workflowId);
const {
  run: runWithReadiness,
  confirmOpen: readinessConfirmOpen,
  issues: readinessIssues,
  advisories: readinessAdvisories,
  submitting: readinessSubmitting,
  runAnyway: readinessRunAnyway,
  cancel: readinessCancel,
} = useRunWithReadinessConfirm();
const getWorkflowRun = useGetWorkflowRun();
const queryClient = useQueryClient();
const alerts = useAlertStore();
const { trackView } = useRecentlyViewed();

const runId = computed(() => (route.params.runid as string) || ""); //TODO: enable from url

// Phase 6 — selected project-context for the builder's run-start. Defaults to the workflow's
// owning project; the user can pick any same-org master/DIY/subscriber project from the
// "Testing Context" dropdown. Persisted per workflow via useBuilderPrefsStore so the choice
// survives navigation, refresh, and tab swaps.
const selectedContextProjectId = ref<string>("");
const selectedTestingContext = computed(
  () => testingContexts.value.find((c) => c.id === selectedContextProjectId.value)?.name ?? ""
);
const canvasMode = ref<"editor" | "history">(
  route.params.view === "history" ? "history" : "editor"
);

// View mode (canvas vs stack) — persisted in builder prefs. When the user hasn't picked
// one yet, fall back to stack on small screens and canvas otherwise.
const viewMode = computed<'canvas' | 'stack'>(
  () => builderPrefs.viewMode ?? (window.innerWidth < 768 ? 'stack' : 'canvas')
);

function toggleViewMode() {
  builderPrefs.setViewMode(viewMode.value === 'canvas' ? 'stack' : 'canvas');
}

// Ref to StackedWorkflowView component
const stackedWorkflowView = ref<InstanceType<typeof StackedWorkflowView> | null>(null);
// Ref to the read-only RunCanvas (history mode) — used to open a node's drill-in on deep-link.
const runCanvas = ref<InstanceType<typeof RunCanvas> | null>(null);

// Refs used to align the zoom controls with the top of the first service panel —
// the RunSummaryHeader's height varies (error row), so we measure the actual top
// of .builder-canvas-inner and expose it as a CSS var the toolbar reads.
const canvasWrapperEl = ref<HTMLElement | null>(null);
const canvasInnerEl = ref<HTMLElement | null>(null);

// The RunSummaryHeader sits between the wrapper and the inner canvas and is behind a v-if — it
// mounts asynchronously once the selected run resolves, and its height changes when an error row
// wraps. Observing it directly (via a function ref that attaches/detaches the ResizeObserver as the
// element appears/disappears) is what fixes the first-load "controls too high" bug: the earlier code
// only measured on mount + a single nextTick, before the header had rendered, so the offset was 0.
const runSummaryHeaderEl = ref<HTMLElement | null>(null);
// Bound as a function ref on the <RunSummaryHeader> component. Component refs hand back the public
// instance, so resolve the root DOM node via $el; a plain element (or null on unmount) is used as-is.
function setRunSummaryHeaderEl(elOrInstance: Element | { $el?: Element } | null) {
  const node = (elOrInstance
    ? ('$el' in elOrInstance ? (elOrInstance.$el as HTMLElement) : (elOrInstance as HTMLElement))
    : null);
  if (runSummaryHeaderEl.value && zoomOffsetObserver) {
    zoomOffsetObserver.unobserve(runSummaryHeaderEl.value);
  }
  runSummaryHeaderEl.value = node;
  if (node && zoomOffsetObserver) {
    zoomOffsetObserver.observe(node);
  }
  // Element just (un)mounted — its presence shifts the inner canvas, so recompute after layout.
  nextTick(updateZoomControlsOffset);
}

// Handle expand/collapse all for stacked view
function handleExpandAll() {
  if (stackedWorkflowView.value) {
    (stackedWorkflowView.value as any).handleExpandAll();
  }
}

function handleCollapseAll() {
  if (stackedWorkflowView.value) {
    (stackedWorkflowView.value as any).handleCollapseAll();
  }
}

const currentNodes = computed(() => {
  if (canvasMode.value === "history" && historyMgmt.selectedRun.value) {
    return historyMgmt.historyNodes.value;
  }
  return nodes.value;
});

const nodes = ref<WorkflowNode[]>([]);
const edges = ref<WorkflowEdge[]>([]);

const savedViewport = ref<{ x: number; y: number; zoom: number } | null>(null);
const isTransitioning = ref(false);
const nodeTypes: any = {
  service: markRaw(ServiceNode),
};
const edgeTypes = {
  animated: markRaw(AnimatedEdge),
};
const hasProjectContext = computed(() => !!route.params.projectid);
const hasWorkflowContext = computed(() => !!workflow.value?.id);
const { projectWorkflows, projectWorkflowsLoading, projectWorkflowsError, refetchProjectWorkflows } = useProjectWorkflows(projectId, ref(true));

// Initialised: stays false during project-workflows fetch, during the brief window
// before the auto-redirect to the first workflow lands, and until the selected workflow
// data has actually arrived (gating on workflow.value presence — not workflowLoading —
// avoids a race where isFetching flips false before data is set, which would race the
// services-sync watch and leave VueFlow mounted with empty nodes).
const initialized = computed(() => {
  if (projectWorkflowsLoading.value) return false;
  if (!workflowId.value && projectWorkflows.value.length > 0) return false;
  if (workflowId.value && !workflow.value) return false;
  return true;
});

// Active/inactive is per-project operational state (does this workflow run?), sourced from the
// project's workflow list and persisted via the mutation below. Kept as a local ref so the toggle
// updates optimistically; the watch re-syncs it whenever the list refetches.
const { setWorkflowActiveAsync } = useSetWorkflowActive(projectId);
const isActive = ref(true);
watch(
  [projectWorkflows, workflowId],
  () => {
    const wf = projectWorkflows.value.find((w) => w.id === workflowId.value);
    if (wf) isActive.value = wf.isActive ?? true;
  },
  { immediate: true }
);

async function handleUpdateIsActive(value: boolean) {
  if (!workflowId.value) return;
  const previous = isActive.value;
  isActive.value = value; // optimistic
  try {
    await setWorkflowActiveAsync({ workflowId: workflowId.value, isActive: value });
  } catch {
    isActive.value = previous; // revert on failure
  }
}

// Per-service dirty-edit Map. Each entry is an in-progress edit that hasn't been
// saved yet. Keyed by serviceId so multiple list-view cards can be edited at once
// without colliding.
const dirtyServices = ref(new Map<string, Service>());

// Canvas-panel binding: dirty version if one exists for the selected id, else the
// workflow's clean copy.
const selectedService = computed<Service | null>({
  get: () => {
    const id = builderState.selectedServiceId.value;
    if (!id) return null;
    const dirty = dirtyServices.value.get(id);
    if (dirty) return dirty;
    return workflow.value?.services?.find(s => s.id === id) ?? null;
  },
  set: (updatedService: Service | null) => {
    const id = builderState.selectedServiceId.value;
    if (!id || !updatedService) return;
    const next = new Map(dirtyServices.value);
    next.set(id, updatedService);
    dirtyServices.value = next;
  }
});

// True when any service has unsaved edits.
const hasUnsavedChanges = computed(() => dirtyServices.value.size > 0);

// True iff the currently-selected (canvas-panel) service is dirty.
const selectedServiceIsDirty = computed(() => {
  const id = builderState.selectedServiceId.value;
  return !!id && dirtyServices.value.has(id);
});

// Services list with any in-progress edits overlaid — used by list view so each
// card reflects its own dirty state without round-tripping through the panel.
const resolvedServices = computed<Service[]>(() => {
  const services = workflow.value?.services ?? [];
  return services.map(s => dirtyServices.value.get(s.id) ?? s);
});

// Set of service ids with unsaved edits — passed into list view so each card
// can display its dirty indicator.
const dirtyServiceIds = computed<Set<string>>(() => new Set(dirtyServices.value.keys()));

// Edit-parents modal source: server-truth services (parentServiceIds is never in the dirty overlay,
// and the modal saves relationships directly through its own endpoint).
const editParentsServices = computed<Service[]>(() => workflow.value?.services ?? []);
const editParentsCurrentIds = computed<string[]>(() => {
  const targetId = builderState.serviceToEditParents.value?.id;
  if (!targetId) return [];
  return editParentsServices.value.find((s) => s.id === targetId)?.parentServiceIds ?? [];
});

// List-view per-card edit handlers
function handleServiceUpdate(serviceId: string, updatedService: Service) {
  const next = new Map(dirtyServices.value);
  next.set(serviceId, updatedService);
  dirtyServices.value = next;
}

function isServiceDirty(serviceId: string): boolean {
  return dirtyServices.value.has(serviceId);
}

// The Filter when popover on the canvas: which child service it edits, and where that service's funnel icon
// was on screen when it was clicked.
const filterPopover = ref<{ serviceId: string; anchor: DOMRect } | null>(null);

// The saved service, as the funnel shows it. The popover edits and saves the saved filter.
const filterPopoverService = computed<Service | null>(() => {
  const opened = filterPopover.value;
  if (!opened) return null;
  return workflow.value?.services?.find((service) => service.id === opened.serviceId) ?? null;
});

// The popover's field offers what the child can read, which is its own ancestors. The panel's scope cannot
// be used here: it follows the selected service, and no service is selected while the popover is open.
const filterPopoverScope = computed<ScopeDescriptor>(() => ({
  roots: buildWorkflowScope(workflow.value, outputSamples.value, slotTemplates.value, filterPopover.value?.serviceId),
  functions: bbelFunctions.value,
}));

// The popover's save has its own mutation, so its error and in-flight state never show in the service panel.
const {
  updateServiceAsync: saveFilterWhenAsync,
  updateServiceError: filterWhenSaveError,
  updateServicePending: filterWhenSaving,
  updateServiceReset: filterWhenSaveReset,
} = useUpdateService(workflowId);

// A failed save's error stays on the mutation, so it is cleared before the popover opens again.
function openFilterPopover(serviceId: string, anchor: DOMRect) {
  filterWhenSaveReset();
  filterPopover.value = { serviceId, anchor };
}

// Save sends only the filter, so any other unsaved edit to the service stays in its draft. That draft takes the
// saved filter, so saving the draft later does not put the old filter back. On failure the popover stays open
// with the error and keeps the typed filter.
async function saveFilterWhen(serviceId: string, expression: string) {
  try {
    await saveFilterWhenAsync({ serviceId, service: { serviceFilterExpression: expression } });
  } catch {
    return; // shown in the popover through filterWhenSaveError
  }
  const draft = dirtyServices.value.get(serviceId);
  if (draft) handleServiceUpdate(serviceId, { ...draft, serviceFilterExpression: expression });
  filterPopover.value = null;
}
const { workflowVersions } = useWorkflowVersions(workflowId);
const versionMgmt = useVersionManagement(workflow, workflowVersions);
const { publishWorkflow, publishWorkflowAsync, publishWorkflowPending, publishWorkflowError, publishWorkflowReset } = usePublishWorkflow();
const { discardWorkflowChanges, discardWorkflowChangesPending, discardWorkflowChangesError, discardWorkflowChangesReset } = useDiscardWorkflowChanges();
const { restoreWorkflowVersion, restoreWorkflowVersionPending, restoreWorkflowVersionError, restoreWorkflowVersionReset } = useRestoreWorkflowVersion();

// Local version management state (not in composable)
const releaseNotes = ref('');
const showDiscardModal = ref(false);
const canvasOps = useWorkflowCanvas(
  workflow,
  versionMgmt.selectedVersion,
  nodes,
  edges,
  currentNodes,
  canvasMode,
  isTransitioning,
  savedViewport,
  setViewport,
  fitView
);

const nodeOps = useNodeOperations(
  workflowId,
  nodes,
  edges,
  getViewport,
  canvasOps.getPositionKey,
  canvasOps.nodePositions,
  canvasOps.flowDirection,
  builderState.serviceToRename,
  builderState.renameInputValue,
  builderState.serviceToDuplicate,
  builderState.serviceToDelete,
  builderState.serviceToToggle,
  builderState.openRenameServiceModal,
  builderState.closeRenameServiceModal,
  builderState.openDuplicateServiceModal,
  builderState.closeDuplicateServiceModal,
  builderState.openDeleteServiceModal,
  builderState.closeDeleteServiceModal,
  builderState.openToggleServiceModal,
  builderState.closeToggleServiceModal,
  builderState.closeAddServicePanel,
  builderState.serviceToEditParents,
  builderState.openEditParentsModal,
  builderState.closeEditParentsModal
);

const workflowOps = useWorkflowOperations();
const historyMgmt = useHistoryManagement(workflowRuns);

const { importWorkflowAsync, importWorkflowPending } = useImportWorkflow(workflowId);

// Export the current workflow as a portable JSON file.
async function onExportWorkflow() {
  try {
    await workflowOps.exportWorkflow(workflowId.value, workflow.value?.name ?? "workflow");
  } catch (error) {
    const apiErr = error as { userMessage?: string };
    alerts.addError("Couldn't export workflow", apiErr?.userMessage ?? "The workflow could not be exported.");
  }
}

// Import replaces THIS workflow's working copy — destructive, so it goes through a confirm modal
// (the app's standard inline-Bootstrap pattern, not a browser confirm). Pick+validate the file first,
// then stash the bundle and open the modal; confirming runs the import.
const showImportConfirmModal = ref(false);
const pendingImportBundle = ref<WorkflowExportBundle | null>(null);

async function onImportWorkflow() {
  const picked = await workflowOps.readImportFile();
  if (!picked.ok) {
    if (!picked.cancelled) alerts.addError("Couldn't import workflow", picked.reason);
    return;
  }
  pendingImportBundle.value = picked.bundle;
  showImportConfirmModal.value = true;
}

function closeImportConfirmModal() {
  showImportConfirmModal.value = false;
  pendingImportBundle.value = null;
}

// The query invalidation in the hook reloads the canvas with the imported services.
async function confirmImportWorkflow() {
  const bundle = pendingImportBundle.value;
  if (!bundle) return;
  try {
    const outcome = await importWorkflowAsync(bundle);
    showImportConfirmModal.value = false;
    pendingImportBundle.value = null;
    if (outcome.validationWarnings.length > 0) {
      alerts.addWarning(
        "Imported with warnings",
        `Fix these before publishing: ${outcome.validationWarnings.join("; ")}`
      );
    } else {
      alerts.addInfo("Workflow imported", "The workflow was replaced with the imported file.");
    }
  } catch (error) {
    const apiErr = error as { userMessage?: string };
    alerts.addError("Couldn't import workflow", apiErr?.userMessage ?? "The workflow could not be imported.");
  }
}

// Debug-only run-animation simulator — feeds fake progress frames through the live-update path so the
// history-mode canvas/list edges animate without a real backend run. Gated on session debug mode (?debug=1).
const debug = useDebugStore();
const runSimulator = useRunSimulator(historyMgmt.selectedRun);

// Per-service "run this service" demo state (useServiceManagement). This is the single-service
// test-run affordance — unrelated to the removed workflow run sub-mode. Backed by local refs.
const serviceRunningNodes = ref<Set<string>>(new Set());
const serviceLiveRunData = ref<Map<string, any>>(new Map());

const serviceMgmt = useServiceManagement(
  nodes,
  serviceRunningNodes,
  serviceLiveRunData,
  builderState.openServiceDetails,
  builderState.openAddServicePanel,
  builderState.openRunServiceModal,
  builderState.closeRunServiceModal,
  builderState.restoreServiceDetailsOnModalClose,
  builderState.runModalWasCancelled,
  builderState.runServiceNodeId,
  builderState.runServiceData
);

// The product a project belongs to — a master publishes one (publisherProductId), a subscriber
// consumes one (subscriberProductId). DIY projects belong to no product.
const productIdOf = (p: { publisherProductId?: string; subscriberProductId?: string }) =>
  p.publisherProductId || p.subscriberProductId || "";

// Phase 6 — testing contexts are the projects that share the current workflow's product, scoped to
// the caller's org (useProjects is already org-scoped server-side). Master first (the publisher's
// "preview against my own config" case), then subscriber projects. A DIY workflow belongs to no
// product, so the only valid context is its own project.
//
// NOTE: partner-org projects are NOT yet included. The intent is to also show projects from partner
// orgs that subscribe to this product, but useProjects()/GET /api/projects is org-scoped server-side
// and the Project type carries no orgId, so cross-org projects never reach the client. That needs a
// backend change (a cross-org endpoint returning partner projects for the product + orgId on the
// response, respecting the partner relationship) before it can be wired up here.
const testingContexts = computed(() => {
  const anchorProductId = productIdOf(
    projects.value.find((p) => p.id === projectId.value) ?? {}
  );
  const list = projects.value
    .filter((p) => p.id) // defensive
    .filter((p) =>
      anchorProductId
        ? productIdOf(p) === anchorProductId
        : p.id === projectId.value
    )
    .map((p) => {
      const type =
        p.projectType === "PublisherMaster"
          ? "master"
          : p.projectType === "DIY"
            ? "diy"
            : "subscriber";
      return {
        id: p.id,
        name: p.name,
        type,
        active: p.id === selectedContextProjectId.value,
      };
    });
  // Order: the workflow's owning project first, then master, DIY, subscriber.
  const orderForType: Record<string, number> = { master: 0, diy: 1, subscriber: 2 };
  return list.sort((a, b) => {
    if (a.id === projectId.value) return -1;
    if (b.id === projectId.value) return 1;
    return (orderForType[a.type] ?? 9) - (orderForType[b.type] ?? 9) || a.name.localeCompare(b.name);
  });
});

// Hydrate selection from the prefs store, falling back to the workflow's owning project.
watch(
  [projectId, workflowId, () => projects.value.length],
  () => {
    if (!projectId.value || projects.value.length === 0) return;
    const stored = workflowId.value ? builderPrefs.testingContextFor(workflowId.value) : null;
    const exists = (id: string) => projects.value.some((p) => p.id === id);
    if (stored && exists(stored)) {
      selectedContextProjectId.value = stored;
    } else if (!selectedContextProjectId.value || !exists(selectedContextProjectId.value)) {
      selectedContextProjectId.value = projectId.value;
    }
  },
  { immediate: true }
);

const currentProject = computed(() => {
  if (!hasProjectContext.value) return null;
  return projects.value.find((p) => p.id === projectId.value) ?? null;
});

const isPublisherMasterProject = computed(() => {
  if (!currentProject.value?.publisherProductId) return false;
  const product = products.value.find(
    (p) => p.id === currentProject.value!.publisherProductId
  );
  if (!product) return false;

  // Check if this project is the master project for the product
  return product.masterProjectId === currentProject.value.id;
});

const currentEdges = computed(() => {
  // History edges carry their own status-derived styling/animation (see useHistoryManagement).
  if (canvasMode.value === "history") return historyMgmt.historyEdges.value;

  // Editor: plain idle connectors — running state now lives entirely in history mode. The flowing dot is
  // switched off rather than left unsaid: the edge ids are the same ones history mode uses, and edge
  // updates are merged by id, so one switched on there would still be on here. Same idle styling the
  // history canvas uses for a finished run — one source of truth (edgeStyleForStatus).
  const idle = edgeStyleForStatus(undefined, false);
  // A relationship line is editable only on an editable draft — never on a read-only published/
  // archived version. The hover controls (edit/delete) and their callbacks ride on edge.data, keyed
  // off this. Edit opens the parent-services modal for the child (target) — a line's parents belong
  // to its child end.
  const editable = canvasMode.value === "editor" && !versionMgmt.isPublishedVersion.value;
  // The icon follows the saved services, as the node badges do, so typing in the service panel does not rebuild
  // every edge. A saved filter shows once the workflow refetch after the save lands.
  const servicesById = new Map((workflow.value?.services ?? []).map((service) => [service.id, service]));
  return edges.value.map((edge) => ({
    ...edge,
    style: idle.style,
    markerEnd: idle.markerEnd,
    data: {
      ...(edge as { data?: Record<string, unknown> }).data,
      ...idle.data,
      editable,
      onDelete: editable ? () => removeRelationship(edge.source, edge.target) : undefined,
      onEdit: editable ? () => nodeOps.handleEditParents(edge.target) : undefined,
      // The funnel icon and its popover. A connector belongs to its child end: edge.target is the child.
      filter: editable ? edgeFilter(servicesById.get(edge.target), edge.source) : undefined,
      onFilter: editable ? (anchor: DOMRect) => openFilterPopover(edge.target, anchor) : undefined,
    },
  }));
});

function getLeafNodeIds(nodeList: any[], edgeList: any[]): Set<string> {
  const nodesWithChildren = new Set(edgeList.map((edge) => edge.source));
  const leafNodes = nodeList
    .filter((node) => !nodesWithChildren.has(node.id))
    .map((node) => node.id);
  return new Set(leafNodes);
}

const leafNodeIds = computed(() => {
  return getLeafNodeIds(currentNodes.value, currentEdges.value);
});

const enhancedNodes = computed(() => {
  return currentNodes.value.map((node) => ({
    ...node,
    data: {
      ...node.data,
      isLeafNode: leafNodeIds.value.has(node.id),
      canvasMode: canvasMode.value,
      flowDirection: canvasOps.flowDirection.value,
      // executionData / executionNote come from node.data.run (history) via the spread above —
      // there's no live-execution overlay any more.
      // Editing actions are available in the editor, disabled in history.
      onAddService: canvasMode.value === "history" ? undefined : serviceMgmt.handleAddService,
      onRunService: canvasMode.value === "history" ? undefined : serviceMgmt.handleRunService,
      onToggleDisabled: canvasMode.value === "history" ? undefined : nodeOps.handleToggleDisabled,
      onDeleteService: canvasMode.value === "history" ? undefined : nodeOps.handleDeleteService,
      onRenameService: canvasMode.value === "history" ? undefined : nodeOps.handleRenameService,
      onDuplicateService: canvasMode.value === "history" ? undefined : nodeOps.handleDuplicateService,
      onEditParents: canvasMode.value === "history" ? undefined : nodeOps.handleEditParents,
    },
  }));
});

function goBack() {
  router.push("/projects");
}

function selectProject(project: any) {
  builderState.selectedProject.value = project;
  builderState.showProjectPicker.value = false;
  router.push(`/projects/${project.id}/builder`);
}


function selectTestingContext(context: any) {
  // Phase 6 — persist the project id so the run-start can include it as projectContextId.
  selectedContextProjectId.value = context.id;
  if (workflowId.value) {
    builderPrefs.setTestingContext(workflowId.value, context.id);
  }
  builderState.showTestingContextDropdown.value = false;
}

function switchToProject(project: any) {
  router.push(`/projects/${project.id}/builder/new`);
}

function createNewWorkflow() {
  if (!hasProjectContext.value) {
    return;
  }

  router.push(`/projects/${projectId.value}/builder/new`);
}


// "Use as new draft" — load the viewed version's content into the working copy as unpublished edits
// (Status → Draft), which the user then reviews and publishes. Overwrites the current draft, so it's
// gated behind the confirm modal. Non-throwing mutate: failures render in the modal's FormAlert.
function openUseAsNewDraftModal() {
  restoreWorkflowVersionReset();
  builderState.openCopyVersionModal();
}

function handleUseAsNewDraft() {
  const versionId = versionMgmt.currentVersion.value?.id;
  if (!workflowId.value || !versionId) return;
  restoreWorkflowVersion(
    { workflowId: workflowId.value, versionId },
    {
      onSuccess: () => {
        builderState.closeCopyVersionModal();
        versionMgmt.selectedVersion.value = 'Draft';
      },
    }
  );
}

// Subscriber forks "Apply" their edits (no audience, version labels are meaningless to them);
// publisher master / DIY "Publish" a version. Mirrors the button label in BuilderHeader.
const isSubscriberContext = computed(() => currentProject.value?.projectType === "Subscriber");

// Shared builder navigation — owns the fork-vs-master decision and the confirm-before-forking flow.
// Used both for the workflow-select dropdown and the edit-transition gate below.
const builderNav = useBuilderNavigation(projectId);

// The row for the workflow currently open, as this project sees it. For a subscriber, a base
// (un-customized) workflow appears here with isCustomized === false; a fork appears customized.
const currentWorkflowRow = computed(() =>
  projectWorkflows.value.find((w) => w.id === workflowId.value) ?? null
);

// An un-forked subscriber base workflow: the subscriber is viewing the publisher's master, read-only.
// Editing it must first fork (confirm-before-forking) — otherwise every save 403s on the edit guard.
const isUnforkedSubscriberBase = computed(
  () => isSubscriberContext.value && currentWorkflowRow.value?.isCustomized === false
);

// Gate the transition into the editor. When on an un-forked base workflow, intercept and open the
// customize confirm instead of dropping the user into an editor whose saves would be rejected.
// Returns true if it handled (blocked) the transition.
function guardEditEntry(): boolean {
  if (!isUnforkedSubscriberBase.value) return false;
  const row = currentWorkflowRow.value;
  if (!row) return false;
  builderNav.requestFork({ id: row.id, name: row.name, isCustomized: row.isCustomized }, "editor");
  return true;
}

const applyAndRun = useApplyAndRun({
  hasDraftChanges: versionMgmt.hasDraftChanges,
  isBlockedFromApply: isUnforkedSubscriberBase,
  applyAsync: () => publishWorkflowAsync(workflowId.value),
  startRun,
  resetApplyError: publishWorkflowReset,
});

// Same gate useApplyAndRun applies, so the "Apply & Run" label never promises an apply that won't fire.
const showApplyAndRun = computed(() => versionMgmt.hasDraftChanges.value && !isUnforkedSubscriberBase.value);

// Publish via mutate (non-throwing): a failed publish-gate validation lands in publishWorkflowError
// and renders in the modal's FormAlert. Success side-effects run only when the gate passes.
function handlePublishVersion() {
  if (!workflowId.value) return;
  publishWorkflow(workflowId.value, {
    onSuccess: () => {
      releaseNotes.value = '';
      closePublishModal();
      versionMgmt.selectedVersion.value = 'Draft';
    },
  });
}

// Open/close the publish modal with a clean error slate so a prior failure doesn't linger.
function openPublishModal() {
  publishWorkflowReset();
  builderState.openPublishVersionModal();
}

function closePublishModal() {
  publishWorkflowReset();
  builderState.closePublishVersionModal();
}

// Discard reverts the working copy to the last published version — destructive, so it's gated behind
// a confirm modal. Non-throwing mutate: a failure lands in discardWorkflowChangesError and renders in
// the modal's FormAlert; success closes the modal (the workflow query invalidation reloads the canvas).
function openDiscardModal() {
  discardWorkflowChangesReset();
  showDiscardModal.value = true;
}

function closeDiscardModal() {
  discardWorkflowChangesReset();
  showDiscardModal.value = false;
}

function handleDiscardChanges() {
  if (!workflowId.value) return;
  discardWorkflowChanges(workflowId.value, {
    onSuccess: () => {
      showDiscardModal.value = false;
      versionMgmt.selectedVersion.value = 'Draft';
    },
  });
}

function openSettings() {}

function dismissActiveModal() {
  showDiscardModal.value = false;
  closeImportConfirmModal();
  builderState.showCopyVersionModal.value = false;
  builderState.showPublishVersionModal.value = false;
  nodeOps.cancelDeleteService();
  nodeOps.cancelToggleService();
  nodeOps.handleCancelRename();
  nodeOps.cancelDuplicateService();
  nodeOps.cancelEditParents();
}

// Workflow management functions — route through the shared resolver so selecting a subscriber's
// already-customized workflow lands on its fork, and a base workflow opens read-only (the edit gate
// then handles forking on an edit attempt).
async function handleSelectWorkflow(option: any) {
  if (!hasProjectContext.value) return;
  const row = projectWorkflows.value.find((w) => w.id === option.id) ?? null;
  await builderNav.openInBuilder(currentProject.value, {
    id: option.id,
    name: row?.name ?? option.name,
    isCustomized: row?.isCustomized,
  }, "editor");
}

// Run history management functions
function getCurrentRun() {
  if (!runId.value) {
    return null;
  }
  console.log(runId);
  return workflowRuns.value.find((run) => run.id === runId.value) || null;
}

// Exit the builder back to the project's Workflows tab. Guards unsaved service-panel edits so
// leaving doesn't silently drop them — matches the panel-close confirm pattern.
function exitBuilder() {
  // History mode is read-only: send the user back to where they came from — the project's run-history
  // tab, with the same workflow's run they were looking at reselected. Editor mode has no run context,
  // so it returns to the workflow list (and guards unsaved edits first).
  if (canvasMode.value === "history") {
    const run = historyMgmt.selectedRun.value;
    if (run) {
      router.push({
        name: "project-history-run",
        params: { projectid: projectId.value, workflowid: run.workflowId, runid: run.id },
      });
    } else {
      router.push({ name: "project-history", params: { projectid: projectId.value } });
    }
    return;
  }

  if (hasUnsavedChanges.value && !confirm('You have unsaved changes. Leave the builder without saving?')) {
    return;
  }
  router.push(`/projects/${projectId.value}/workflows`);
}

function exitHistoryMode() {
  if (!hasProjectContext.value) {
    return;
  }

  // Confirm-before-forking: leaving history into the editor on an un-forked subscriber base workflow
  // pops the customize confirm instead of opening an editor whose saves would be rejected.
  if (guardEditEntry()) return;

  savedViewport.value = getViewport();
  canvasOps.saveNodePositions();
  isTransitioning.value = true;

  router.push(`/projects/${projectId.value}/builder/${workflowId.value}/editor`);
}

async function loadRunState() {
  if (!runId.value) {
    historyMgmt.selectedRun.value = null;
    return;
  }
  // First try the cached list. This is the common case — the user clicked a run
  // in the panel or refreshed a page where the run is still in the active filter.
  const fromList = getCurrentRun();
  if (fromList) {
    historyMgmt.selectedRun.value = fromList;
    return;
  }
  // Deep-link case: the URL refers to a run that isn't in the current filter set
  // (e.g. status/time filter excludes it, or page-size cap dropped it). Fetch the
  // single run by id so the right pane shows it regardless of filter state. Don't
  // kick the user back to editor — they explicitly asked for this run.
  try {
    const fetched = await getWorkflowRun(runId.value);
    // The single-run read is only org-scoped (GetById), so a deep-linked runId from another project
    // of the same workflow would otherwise resolve. Reject any run that didn't execute under this
    // project — the builder list is project-scoped, and the deep-link must match it.
    if (fetched && runId.value === fetched.id && fetched.runningProjectId === projectId.value) {
      historyMgmt.selectedRun.value = fetched;
    } else {
      historyMgmt.selectedRun.value = null;
    }
  } catch {
    // Treat fetch failure as "run not found" — clear selection. Stay in history
    // mode so the empty state surfaces with the filters and the user can adjust.
    historyMgmt.selectedRun.value = null;
  }
}

function setCanvasMode(mode: "editor" | "history") {
  // Confirm-before-forking: switching into the editor on an un-forked subscriber base workflow pops
  // the customize confirm rather than dropping into a non-savable editor. History stays open freely.
  if (mode === "editor" && guardEditEntry()) return;

  // Set the reactive canvas mode
  canvasMode.value = mode;

  // Reflect mode in the URL
  router.replace({
    params: { ...route.params, view: mode, runid: mode === "editor" ? undefined : route.params.runid },
  });

  // Use the base canvas mode switching from composable
  canvasOps.setCanvasMode(mode);

  // Additional BuilderView-specific logic for history mode
  if (mode === "history") {
    if (runId.value) {
      // Specific run requested in URL — load it (fetches separately if it's
      // outside the current filter set so deep links always resolve).
      loadRunState();
    } else if (historyMgmt.filteredRunHistory.value.length > 0) {
      // No run requested — auto-select the first run from the sorted filtered
      // list (newest first by default). Using filteredRunHistory rather than the
      // raw workflowRuns ensures we honour the current sort.
      const firstRun = historyMgmt.filteredRunHistory.value[0];
      historyMgmt.selectedRun.value = firstRun;
      historyMgmt.selectRunFromUrl(firstRun);
      router.replace({ params: { ...route.params, view: "history", runid: firstRun.id } });
    } else {
      // Filter returned nothing and no run requested — let the empty-state overlay show.
      isTransitioning.value = false;
      return;
    }
  } else {
    if (runId.value) {
      exitHistoryMode();
    }
  }
}

// Save service changes to backend. serviceId is optional — when omitted, falls back
// to the currently-selected canvas service. On failure the service stays dirty and the panel's
// FormAlert shows the mutation error (updateServiceError).
async function handleServiceSave(serviceId?: string) {
  const id = serviceId ?? builderState.selectedServiceId.value;
  if (!id) return;
  const dirty = dirtyServices.value.get(id);
  if (!dirty) return;
  try {
    await updateServiceAsync({ serviceId: id, service: dirty });
  } catch {
    return; // surfaced via updateServiceError in the panel
  }
  const next = new Map(dirtyServices.value);
  next.delete(id);
  dirtyServices.value = next;
}

// Phase 4 — fire the IsComponent toggle immediately (no need to wait for the broader Save).
async function handleToggleComponent(serviceId: string, isComponent: boolean) {
  try {
    await setServiceIsComponentAsync({ serviceId, isComponent });
  } catch {
    // Mutation error surface; the watch on workflow data will reconcile on next refetch.
  }
}

// Close handler with unsaved changes warning (canvas panel)
function handleServicePanelClose() {
  const id = builderState.selectedServiceId.value;
  if (id && dirtyServices.value.has(id) && !confirm('Discard unsaved changes?')) {
    return; // Keep panel open
  }
  if (id) {
    const next = new Map(dirtyServices.value);
    next.delete(id);
    dirtyServices.value = next;
  }
  builderState.closeServiceDetails();
}

// Discard handler — drops in-progress edits without closing the panel/card.
function handleServiceDiscard(serviceId?: string) {
  const id = serviceId ?? builderState.selectedServiceId.value;
  if (!id) return;
  const next = new Map(dirtyServices.value);
  next.delete(id);
  dirtyServices.value = next;
}

function handleNodeClick(nodeId: string, nodeData: any) {
  if (canvasMode.value === "history") {
    builderState.openRunServiceDetails(nodeId, nodeData);
  } else {
    builderState.openServiceDetails(nodeId);
  }
}

// Per-service debounce timers for position saves. A drag fires one drag-stop, but dragging the same
// node twice quickly would fire two saves; coalescing to the last keeps it to one PUT per resting spot.
const positionSaveTimers = new Map<string, ReturnType<typeof setTimeout>>();
const POSITION_SAVE_DEBOUNCE_MS = 400;

// Persist a service's canvas position. Best-effort: a failed layout save is not worth interrupting the
// user over — the position stays put locally and the next successful edit's refetch reconciles it. The
// in-flight guard stops that refetch from yanking the node back to the server's older coordinates.
async function persistNodePosition(serviceId: string, position: { x: number; y: number }) {
  positionSaveInFlightNodeId.value = serviceId;
  try {
    await updateServiceAsync({
      serviceId,
      service: { canvasLayout: { x: position.x, y: position.y } },
    });
  } catch {
    updateServiceReset();
  } finally {
    if (positionSaveInFlightNodeId.value === serviceId) positionSaveInFlightNodeId.value = null;
  }
}

function scheduleNodePositionSave(serviceId: string, position: { x: number; y: number }) {
  const existing = positionSaveTimers.get(serviceId);
  if (existing) clearTimeout(existing);
  positionSaveTimers.set(
    serviceId,
    setTimeout(() => {
      positionSaveTimers.delete(serviceId);
      // The node may have been deleted (or the workflow discarded) during the debounce — don't persist
      // a position for a service that's gone, which would also leave the in-flight guard stuck on it.
      const stillExists = nodes.value.some((n) => (n.data.serviceId ?? n.id) === serviceId);
      if (stillExists) void persistNodePosition(serviceId, position);
    }, POSITION_SAVE_DEBOUNCE_MS)
  );
}

// Drag end on the editor canvas. VueFlow tracks the drag on its own copy; write the resting position
// back into nodes.value (so a later refetch/recompute renders where the user left it) and persist it so
// the arrangement survives reload and reaches other editors. Never persist in history/published mode.
function handleNodeDragStop(event: any) {
  canvasOps.onNodeDragStop(event);
  if (canvasMode.value !== "editor" || versionMgmt.isPublishedVersion.value) return;

  const draggedNode = event.node;
  const node = nodes.value.find((n) => n.id === draggedNode.id);
  if (!node) return;

  node.position = { ...draggedNode.position };
  const serviceId = node.data.serviceId ?? node.id;
  scheduleNodePositionSave(serviceId, node.position);
}

// Auto-arrange (toolbar dagre button). The layout writes fresh positions onto nodes.value; persist all
// of them so the arranged layout is itself saved — otherwise the next edit's refetch would reset it.
async function handleApplyHierarchicalLayout() {
  canvasOps.applyHierarchicalLayout();
  if (canvasMode.value !== "editor" || versionMgmt.isPublishedVersion.value) return;
  await nextTick();
  await persistAllNodePositions();
}

// Persist every editor node's current position (used by auto-arrange and toggle-direction, which both
// recompute the whole layout). Sequential to stay within one-aggregate-per-save discipline; best-effort
// per node — a single failure doesn't abort the rest.
async function persistAllNodePositions() {
  for (const node of nodes.value) {
    const serviceId = node.data.serviceId ?? node.id;
    await persistNodePosition(serviceId, node.position);
  }
}

// Toggle flow direction (TB/LR). One stored arrangement (per the design): flipping direction always
// re-runs dagre for the new orientation and persists the result — so the saved layout matches what's on
// screen. (This deliberately overwrites prior manual tweaks; TB and LR don't keep separate layouts.)
async function handleToggleFlowDirection() {
  canvasOps.flowDirection.value = canvasOps.flowDirection.value === "TB" ? "LR" : "TB";
  canvasOps.applyHierarchicalLayout();
  if (canvasMode.value !== "editor" || versionMgmt.isPublishedVersion.value) return;
  await nextTick();
  await persistAllNodePositions();
}

// Re-run (history mode): open the same Run Workflow modal the project History tab uses — inputs
// prefilled from the selected run, plus the readiness confirm gate. Unlike the History tab we pass
// stay-on-success so the modal doesn't navigate to the project run page; onRerunStarted keeps the
// user on the canvas and loads the new run in history mode instead.
const rerunModalOpen = ref(false);
const rerunWorkflowId = ref<string | null>(null);
const rerunWorkflowName = ref<string | undefined>(undefined);
const rerunInputs = ref<Record<string, unknown>>({});

// The inputs a run was started with are read on demand, not carried on the list row — they live on the
// run's context, so a page of rows would be a document read each.
async function rerunSelectedRun() {
  const run = historyMgmt.selectedRun.value;
  if (!run) return;
  rerunWorkflowId.value = run.workflowId;
  rerunWorkflowName.value = run.workflowName;
  rerunInputs.value = {};
  rerunModalOpen.value = true;

  const detail = await getWorkflowRun(run.id);
  // Only fill the run the user actually asked for — they can pick another while this is in flight.
  if (rerunModalOpen.value && rerunWorkflowId.value === run.workflowId)
    rerunInputs.value = detail.inputs ?? {};
}

async function onRerunStarted(newRunId: string) {
  // Stay in the builder: point the URL at the new run and load it onto the canvas in history mode.
  // loadRunState fetches the run by id directly, so it resolves even before the runs list refetches.
  await router.replace({ params: { ...route.params, view: "history", runid: newRunId } });
  await loadRunState();
}

// Synthetic "starting" placeholder id — a run that hasn't been assigned a real id yet. Kept
// out of the URL/history list; only lives in selectedRun until the real run resolves.
const PENDING_RUN_ID = "__pending__";

/**
 * Build an optimistic in-progress run from the current workflow's services so History can render
 * the running animation the instant the user clicks Run — the start POST is synchronous and takes a
 * few seconds, and waiting for it before switching made the click feel like a no-op. Every service
 * shows as Running; the real run replaces this the moment the POST returns.
 */
function buildStartingRun(): RunHistory {
  const services = (workflow.value?.services ?? []).map((s) => ({
    serviceId: s.id,
    serviceName: s.name,
    serviceType: s.serviceModelType?.$type ?? "",
    status: "Running" as const,
    total: 0,
    succeeded: 0,
    warning: 0,
    failed: 0,
    neverResponded: 0,
    expected: 0,
    processingMs: 0,
    achievedConcurrency: null,
    parentServiceIds: s.parentServiceIds ?? [],
  }));
  return {
    id: PENDING_RUN_ID,
    workflowId: workflowId.value,
    // The placeholder run executes under the project the builder is open in — same scope the run
    // list is now filtered by, so it belongs on the list and survives the deep-link project guard.
    runningProjectId: projectId.value,
    workflowName: workflow.value?.name ?? "",
    startedAt: new Date(),
    completedAt: new Date(),
    durationInMilliSeconds: 0,
    status: "Running",
    services,
    errors: [],
    inputs: {},
  };
}

function handleExecuteWorkflow() {
  void applyAndRun.execute();
}

async function startRun(){
  // Start a run on the workflow-runs resource. The owning project comes from the route (falling
  // back to the workflow's own project); selectedContextProjectId carries the optional Phase-6
  // config override (it defaults to the owning project, so an explicit value only differs when the
  // user previews against another project).
  const owningProjectId = projectId.value || workflow.value?.projectId || "";

  // Optimistically switch to history with an in-progress placeholder so the click gives immediate
  // feedback while the (synchronous) start POST runs. On cancel/error we revert to the editor below.
  const wasCanvasMode = canvasMode.value;
  canvasMode.value = "history";
  canvasOps.setCanvasMode("history");
  historyMgmt.selectedRun.value = buildStartingRun();

  function revertToEditor() {
    historyMgmt.selectedRun.value = null;
    canvasMode.value = wasCanvasMode;
    canvasOps.setCanvasMode(wasCanvasMode);
  }

  let newRunId: string;
  try {
    // Safe-by-default (ADR-033): blocking config gaps open the pre-run confirm; "Run anyway" overrides.
    // A cancel throws RunCancelledError — a deliberate no-op; revert to the editor.
    newRunId = await runWithReadiness({
      workflowId: workflowId.value,
      projectId: owningProjectId,
      projectContextId: selectedContextProjectId.value || undefined,
    });
  } catch (error) {
    revertToEditor();
    if (error instanceof RunCancelledError) return;
    // A real start failure — surface it and stay in the editor (don't switch to history).
    const apiErr = error as { userMessage?: string; message?: string };
    alerts.addError("Couldn't start run", apiErr?.userMessage ?? apiErr?.message ?? "The run could not be started.");
    return;
  }

  // The run exists and the engine has it. Point the URL at the real id and load it — that swaps the
  // placeholder for the real run, which is what starts the progress feed following it. The list refetch
  // and the output samples are the completion's business, not this call's.
  void queryClient.invalidateQueries({ queryKey: ["workflow-runs", projectId.value, workflowId.value] });
  await router.replace({ params: { ...route.params, view: "history", runid: newRunId } });
  await loadRunState();
}

function toMs(date: string | Date | null | undefined): number {
  if (!date) return 0;
  return date instanceof Date ? date.getTime() : new Date(date).getTime();
}



function handleSelectHistoryRun(run: RunHistory){
  // A child belongs to another workflow; keep its URL and canvas in that workflow's history.
  if (run.workflowId !== workflowId.value) {
    router.push({ name: "project-history-run", params: {
      projectid: run.runningProjectId || projectId.value, workflowid: run.workflowId, runid: run.id,
    } });
    return;
  }
  historyMgmt.selectRun(run);
  router.replace({ params: { ...route.params, view: "history", runid: run.id } });
}


// Watch view param — keeps canvasMode in sync when navigating via breadcrumbs or browser back/forward
watch(
    () => route.params.view,
    async (newView) => {
      const mode = (newView === "history" ? "history" : "editor") as "editor" | "history";
      if (canvasMode.value === mode) return;
      canvasMode.value = mode;
    }
);

// Watch canvasMode — defaults to first run when switching to history.
// Uses filteredRunHistory so we pick the newest run honouring the current sort,
// not whatever happened to come first in the raw server response.
watch(
    () => canvasMode.value,
    (newMode) => {
      if (newMode === 'history') {
        if (historyMgmt.filteredRunHistory.value.length > 0 && !historyMgmt.selectedRun.value) {
          const firstRun = historyMgmt.filteredRunHistory.value[0];
          historyMgmt.selectedRun.value = firstRun;
          historyMgmt.selectRunFromUrl(firstRun);
          router.replace({ params: { ...route.params, view: "history", runid: firstRun.id } });
        }
      }
    }
);

// Watch runid + workflowRuns — handles run selection on initial deep-link, when runs load
// asynchronously, and when navigating via breadcrumbs, browser back/forward, or clicking a run.
// Also covers the case where a filter change (e.g. "Reset filters" from the empty-state
// overlay) brings new runs in — when we're in history mode without a specific run requested,
// auto-select the newest matching run so the right pane shows something instead of staying
// stuck on the empty state.
watch(
    [runId, workflowRuns],
    ([newRunId]) => {
      if (!hasProjectContext.value) return;
      if (newRunId) {
        loadRunState();
      } else if (
        canvasMode.value === "history" &&
        historyMgmt.filteredRunHistory.value.length > 0
      ) {
        const firstRun = historyMgmt.filteredRunHistory.value[0];
        historyMgmt.selectedRun.value = firstRun;
        historyMgmt.selectRunFromUrl(firstRun);
        router.replace({ params: { ...route.params, view: "history", runid: firstRun.id } });
      } else {
        historyMgmt.selectedRun.value = null;
      }
      nextTick(() => setTimeout(() => canvasOps.restoreViewport(), 100));
    },
    { immediate: true }
);

// A relationship lives on the child as a parentServiceIds entry. Both persist helpers read the
// current parent set from the service (server truth), never from the edge list — the edge array can
// hold an optimistic entry, and compounding that into the PUT would drop a real parent.
function childServiceContext(childNodeId: string) {
  const childNode = nodes.value.find((n) => n.id === childNodeId);
  const childServiceId = childNode?.data.serviceId ?? childNodeId;
  const childService = workflow.value?.services?.find((s) => s.id === childServiceId);
  return { childServiceId, existingParents: childService?.parentServiceIds ?? [] };
}

// VueFlow nudges node positions when the edge set changes; snapshot and restore so a drawn or
// deleted line never reflows the canvas.
function preserveNodePositions() {
  const snapshot = new Map<string, { x: number; y: number }>();
  nodes.value.forEach((node) => snapshot.set(node.id, { ...node.position }));
  nextTick(() => {
    nodes.value.forEach((node) => {
      const original = snapshot.get(node.id);
      if (original && (node.position.x !== original.x || node.position.y !== original.y)) {
        node.position = original;
      }
    });
  });
}

// Persist a relationship drawn between two handles. connection.source = drag origin = parent
// (output handle); connection.target = drag end = child (input handle). The child stores the parent.
async function onConnect(connection: any) {
  if (canvasMode.value !== "editor" || versionMgmt.isPublishedVersion.value) return;

  const parentId = connection.source;
  const childId = connection.target;
  if (!parentId || !childId || parentId === childId) return;

  const { childServiceId, existingParents } = childServiceContext(childId);
  if (existingParents.includes(parentId)) return; // already a parent — no-op

  const edgeId = `e${parentId}->${childId}`;
  if (edges.value.some((e) => e.id === edgeId)) return;

  // Optimistic add; the identical edge id makes the post-save rebuild seamless.
  preserveNodePositions();
  edges.value.push({ id: edgeId, source: parentId, target: childId, type: "animated" });

  try {
    await updateServiceAsync({
      serviceId: childServiceId,
      service: { parentServiceIds: [...existingParents, parentId] },
    });
  } catch (e) {
    edges.value = edges.value.filter((edge) => edge.id !== edgeId);
    const detail = (e as { userMessage?: string; message?: string })?.userMessage ?? (e as Error)?.message ?? "";
    const message = /cycle|loop/i.test(detail)
      ? "That would create a loop between services."
      : detail || "That connection could not be created.";
    alerts.addWarning("Can't create that connection", message);
    updateServiceReset();
  }
}

// Delete one relationship line (the hover x on an edge). Immediate, no confirm — reversible by
// redrawing. Strips the parent from the child and persists the reduced set.
async function removeRelationship(parentId: string, childId: string) {
  if (canvasMode.value !== "editor" || versionMgmt.isPublishedVersion.value) return;

  const { childServiceId, existingParents } = childServiceContext(childId);
  if (!existingParents.includes(parentId)) return; // already gone

  const edgeId = `e${parentId}->${childId}`;
  const removedEdge = edges.value.find((edge) => edge.id === edgeId);

  preserveNodePositions();
  edges.value = edges.value.filter((edge) => edge.id !== edgeId);

  try {
    await updateServiceAsync({
      serviceId: childServiceId,
      service: { parentServiceIds: existingParents.filter((p) => p !== parentId) },
    });
    alerts.addInfo("Connection removed", "The relationship was deleted.");
  } catch (e) {
    if (removedEdge) edges.value.push(removedEdge);
    const message = (e as { userMessage?: string; message?: string })?.userMessage ?? "Please try again.";
    alerts.addError("Couldn't remove connection", message);
    updateServiceReset();
  }
}

function selectRun(run: any) {
  // Use URL-based navigation for run selection
  historyMgmt.selectRunFromUrl(run);
}

// Live progress for the run on screen. The canvas nodes and the connecting lines are computed off
// selectedRun (useHistoryManagement), so a frame that replaces it redraws the run without a refetch.
// The history panel list and the canvas are two views of the same run, so a live frame must land on
// both — otherwise the panel row sits on its starting status until a refetch, showing a finished run
// as still Running. Patches every cached filter variant for this (project, workflow) (mirrors
// HistoryTab's applyToRunList against project-runs); the partial key matches all query-string variants.
function applyToRunList<TUpdate extends { runId: string }>(
  update: TUpdate,
  apply: (previous: RunHistory | null, update: TUpdate) => RunHistory | null
) {
  queryClient.setQueriesData<Paged<RunHistory>>(
    { queryKey: ["workflow-runs", projectId.value, workflowId.value] },
    (page) => {
      if (!page) return page;
      const index = page.items.findIndex((run) => run.id === update.runId);
      if (index < 0) return page;
      const applied = apply(page.items[index], update);
      if (!applied) return page;
      const items = [...page.items];
      items[index] = applied;
      return { ...page, items };
    }
  );
}

const runProgress = useRunProgress({
  onProgress: (update) => {
    applyToRunList(update, applyProgress);
    if (historyMgmt.selectedRun.value?.id !== update.runId) return;
    const applied = applyProgress(historyMgmt.selectedRun.value, update);
    if (applied) historyMgmt.selectedRun.value = applied;
  },
  onServiceCounts: (update) => {
    applyToRunList(update, applyServiceCounts);
    if (historyMgmt.selectedRun.value?.id !== update.runId) return;
    const applied = applyServiceCounts(historyMgmt.selectedRun.value, update);
    if (applied) historyMgmt.selectedRun.value = applied;
  },
  onFinished: async (finished) => {
    // Key is ["workflow-runs", projectId, workflowId, …]; invalidate by the (project, workflow) prefix
    // so the now project-scoped builder list refetches when a run finishes.
    void queryClient.invalidateQueries({ queryKey: ["workflow-runs", projectId.value, finished.workflowId] });
    // Frames are lossy, and duration, completedAt and the final counts live on the run document —
    // so the last word is a read, not the last frame.
    const fetched = await getWorkflowRun(finished.runId);
    if (fetched && historyMgmt.selectedRun.value?.id === fetched.id) {
      historyMgmt.selectedRun.value = fetched;
    }
    // Output samples only exist once the services have produced something.
    void refetchOutputSamples();
  },
});

// Follow whichever run is on screen while it is still going. One rule covers every way a run comes to
// be selected — clicked in the history panel, deep-linked, or just started from the toolbar.
//
// Stated as "not finished" rather than as a list of live statuses, and that is the point: a run the
// engine has not admitted yet reads Queued, one in flight reads Pending, and Running is only ever the
// optimistic placeholder's own word. Naming the live ones instead would leave a Queued run unwatched —
// and since the only thing that would move it off Queued on screen is a frame, it would never be
// watched at all. Anything the engine adds short of finishing is covered by construction.
watch(
  [() => historyMgmt.selectedRun.value?.id, () => historyMgmt.selectedRun.value?.status],
  ([id, status]) => {
    const stillGoing = !!status && !isFinishedRunStatus(status);
    void runProgress.watch(id && id !== PENDING_RUN_ID && stillGoing ? [id] : []);
  },
  { immediate: true }
);

// Live feed down while the run on the canvas is still going — say so, else it looks frozen-but-done.
const liveConnectionLost = computed(() => {
  const run = historyMgmt.selectedRun.value;
  return (
    canvasMode.value === "history" &&
    !!runProgress.connectionError.value &&
    !!run &&
    !isFinishedRunStatus(run.status)
  );
});

function exportRunData() {
  if (!historyMgmt.selectedRun.value) return;
}

// Auto-select first workflow when landing on /builder or /builder/editor with no workflowid.
// If the project has zero workflows, the template renders the empty state instead.
watch(
  [projectWorkflows, projectWorkflowsLoading, () => route.params.workflowid],
  ([wfs, loading, currentId]) => {
    if (loading) return;
    if (currentId) return;
    if (!wfs.length) return;
    router.replace(`/projects/${projectId.value}/builder/${wfs[0].id}/editor`);
  },
  { immediate: true }
);

let zoomOffsetObserver: ResizeObserver | null = null;

function updateZoomControlsOffset() {
  if (!canvasInnerEl.value || !canvasWrapperEl.value) return;
  const innerTop = canvasInnerEl.value.getBoundingClientRect().top;
  const wrapperTop = canvasWrapperEl.value.getBoundingClientRect().top;
  canvasWrapperEl.value.style.setProperty(
    '--zoom-controls-top',
    `${Math.max(0, innerTop - wrapperTop)}px`
  );
}

onMounted(() => {
  // Sync URL view param
  const initialView = route.params.view as string | undefined;
  if (!initialView) {
    const mode = route.params.runid ? "history" : "editor";
    router.replace({ params: { ...route.params, view: mode } });
  }

  // Track .builder-canvas-inner position so the zoom-controls align with the top
  // of the first service panel regardless of RunSummaryHeader height.
  if (canvasWrapperEl.value && canvasInnerEl.value) {
    zoomOffsetObserver = new ResizeObserver(updateZoomControlsOffset);
    zoomOffsetObserver.observe(canvasWrapperEl.value);
    zoomOffsetObserver.observe(canvasInnerEl.value);
    // If the run-summary header already mounted before this observer existed (its function ref runs
    // during render), pick it up now so its height changes drive the offset too.
    if (runSummaryHeaderEl.value) zoomOffsetObserver.observe(runSummaryHeaderEl.value);
    updateZoomControlsOffset();
  }
});

// Recompute on canvas-mode / selected-run changes, since the RunSummaryHeader
// mounts/unmounts and its height changes with the error row.
watch(
  [() => canvasMode.value, () => historyMgmt.selectedRun.value?.id, () => historyMgmt.selectedRun.value?.errors?.length],
  () => {
    nextTick(updateZoomControlsOffset);
  }
);

// Auto-focus and select text when rename modal opens
watch(() => builderState.showRenameServiceModal.value, async (show) => {
  if (show) {
    await nextTick();
    const input = document.getElementById(
        "serviceNameInput"
    ) as HTMLInputElement;
    if (input) {
      input.focus();
      input.select();
    }
  }
});

// Watch for route changes to handle navigation to builder without project context
watch(
  () => route.params.projectid,
  (newProjectId) => {
    if (!newProjectId) {
      builderState.showProjectPicker.value = true;
    } else {
      builderState.showProjectPicker.value = false;
    }
  },
  { immediate: true }
);

// Track workflow opens in Recently Viewed. Keys off the loaded workflow (not the URL param)
// so we record the real name once the query resolves, and skip the /new placeholder.
watch(
  workflow,
  (w) => {
    if (!w?.id || workflowId.value === "new") return;
    trackView({
      id: w.id,
      name: w.name,
      type: "workflow",
      route: `/projects/${projectId.value}/builder/${w.id}`,
      description: currentProject.value?.name
        ? `Project: ${currentProject.value.name}`
        : undefined,
      icon: "bi-diagram-3",
      metadata: {
        projectId: projectId.value,
        projectName: currentProject.value?.name,
      },
    });
  },
  { immediate: true }
);

onUnmounted(() => {
  zoomOffsetObserver?.disconnect();
  zoomOffsetObserver = null;

  // Flush any position save still waiting on its debounce so a drag right before leaving isn't lost,
  // then drop the timers.
  positionSaveTimers.forEach((timer, serviceId) => {
    clearTimeout(timer);
    const node = nodes.value.find((n) => (n.data.serviceId ?? n.id) === serviceId);
    if (node) void persistNodePosition(serviceId, node.position);
  });
  positionSaveTimers.clear();
});



function mapServiceToNodeData(service: Service) {
  return {
    serviceId: service.id,
    label: service.name,
    description: "",
    subtitle: serviceTypeDisplay(service.serviceModelType?.$type).name,
    serviceType: service.serviceModelType?.$type,
    status: "Active",
    version: "V2.3.0",
    processing: false,
    running: false,
    completed: false,
    failed: false,
    disabled: false,
    isFirstNode: service.id === workflow.value?.rootServiceId,
    badges: nodeBadges(service),
  };
}

// The service whose drag position is currently being saved. Its node keeps its optimistic (dragged)
// position through the mutation's refetch instead of being re-seated on the server's older value.
const positionSaveInFlightNodeId = ref<string | null>(null);

// Incrementally syncs the editor canvas to whatever workflow.services currently looks like.
// Extracted from the watch below so it can also be invoked on canvasMode → editor — see
// the watch right below this function for the "why" (the deps watch can miss a tick when
// TQ hands us cached data and Vue's reactivity dedupes the source).
function syncNodesFromWorkflowServices() {
  const services = workflow.value?.services;
  if (!services) return;

  const serviceIds = new Set(services.map(s => s.id));

  // Remove nodes for deleted services
  nodes.value = nodes.value.filter(n => serviceIds.has(n.id));

  // Add nodes for new services. A service the server has a saved position for lands there; the rest
  // get a placeholder the auto-layout button can tidy. The server position is what makes a
  // relationship edit (which refetches the workflow) leave the arrangement untouched.
  const existingIds = new Set(nodes.value.map(n => n.id));
  const ySpacing = 180;
  const xPosition = 200;
  services
    .filter(s => !existingIds.has(s.id))
    .forEach((service, newIdx) => {
      const positionIndex = existingIds.size + newIdx;
      const fallback = { x: xPosition, y: 120 + positionIndex * ySpacing };
      nodes.value.push({
        id: service.id,
        type: "service",
        position: storedCanvasPosition(service) ?? fallback,
        data: mapServiceToNodeData(service),
      });
    });

  // Update existing nodes: refresh data (name/type changes), and re-seat them on the server's saved
  // position when it has one. Without this, a node dragged this session but whose drag hasn't yet
  // round-tripped would keep its old in-memory spot after a refetch; with it, server truth wins.
  nodes.value.forEach(node => {
    const service = services.find(s => s.id === node.id);
    if (!service) return;
    node.data = mapServiceToNodeData(service);
    const stored = storedCanvasPosition(service);
    // Don't fight a drag/save that's still settling — the mid-flight node keeps its optimistic spot.
    // The in-flight id is a service id, so resolve the node's the same way the save does.
    const serviceId = node.data.serviceId ?? node.id;
    if (stored && serviceId !== positionSaveInFlightNodeId.value) {
      node.position = stored;
    }
  });

  // Rebuild edges from parentServiceIds relationships
  edges.value = services.flatMap(s =>
    s.parentServiceIds.map(parentId => ({
      id: `e${parentId}->${s.id}`,
      source: parentId,
      target: s.id,
      type: "animated",
    }))
  );
}

// Smart watch: incrementally syncs canvas when TQ delivers/updates workflow services.
// `immediate` is essential: when TQ already has the workflow cached (e.g. clicking Edit on a
// customized fork you've opened before this session), `workflow.value.services` is populated
// synchronously before this watch registers, so there's no change to react to. Without immediate
// the canvas stays empty ("No Services Yet") on cache-warm navigation, while a cold URL reload
// works because the fetch resolves after mount and the undefined→array transition fires the watch.
watch(() => workflow.value?.services, () => syncNodesFromWorkflowServices(), { deep: true, immediate: true });

// Re-sync when leaving history mode for editor. The plain services watch above only fires
// on a change in source value, so if nodes.value has gotten out of step (e.g. zero items
// while the user was viewing history) it can show "No Services Yet" until something
// actually mutates the services array. Forcing a sync here keeps the editor canvas honest.
watch(
  () => canvasMode.value,
  (mode) => {
    if (mode === "editor") syncNodesFromWorkflowServices();
  }
);
</script>

<template>
  <!-- Initial load: skeleton (also covers brief redirect-to-first-workflow window) -->
  <div v-if="!initialized" class="builder-container d-flex flex-column p-4">
    <SkeletonLoader
      type="header"
      :error="projectWorkflowsError"
      @retry="refetchProjectWorkflows"
    />
    <SkeletonLoader type="panel" :lines="10" class="mt-4" />
  </div>

  <!-- No workflows in this project: empty state -->
  <div
    v-else-if="!projectWorkflows.length"
    class="builder-container d-flex flex-column justify-content-center align-items-center"
  >
    <div class="text-center">
      <i class="bi bi-bricks display-1 text-muted mb-3"></i>
      <h3 class="text-muted mb-3">No Workflows Yet</h3>
      <p class="text-muted mb-4">Create your first workflow to get started.</p>
      <button class="bb-btn-primary-cta" @click="createNewWorkflow">
        <i class="bi bi-plus-lg me-1"></i>New Workflow
      </button>
    </div>
  </div>

  <div class="builder-container d-flex flex-column" v-else-if="hasWorkflowContext">
    <BuilderHeader
      :hasProjectContext="hasProjectContext"
      :currentProject="currentProject"
      :projectId="projectId"
      :canvasMode="canvasMode"
      :selectedWorkflow="{ id: workflow?.id ?? '', name: workflow?.name ?? '' }"
      :workflowsddl="projectWorkflows"
      :projectsddl="projects"
      :selectedVersion="versionMgmt.selectedVersion.value"
      :selectedVersionText="versionMgmt.selectedVersionText.value"
      :versions="versionMgmt.versions.value"
      :isPublisherMasterProject="isPublisherMasterProject"
      :selectedRun="historyMgmt.selectedRun.value"
      :isActive="isActive"
      :isPublishedVersion="versionMgmt.isPublishedVersion.value"
      :hasDraftChanges="versionMgmt.hasDraftChanges.value"
      :hasPublishedVersions="versionMgmt.hasPublishedVersions.value"
      :showTestingContextDropdown="builderState.showTestingContextDropdown.value"
      :selectedTestingContext="selectedTestingContext"
      :testingContexts="testingContexts"
      :currentVersionUsage="null"
      :currentVersion="versionMgmt.currentVersion.value"
      @openProjectPicker="builderState.openProjectPicker"
      @handleSelectWorkflow="handleSelectWorkflow"
      @createNewWorkflow="createNewWorkflow"
      @selectVersion="versionMgmt.selectVersion"
      @handleAddServiceFromMenu="serviceMgmt.handleAddServiceFromMenu"
      @publishVersion="openPublishModal"
      @discardChanges="openDiscardModal"
      @copyVersion="openUseAsNewDraftModal"
      @toggleTestingContextDropdown="builderState.toggleTestingContextDropdown"
      @selectTestingContext="selectTestingContext"
      @importWorkflow="onImportWorkflow"
      @exportWorkflow="onExportWorkflow"
      @rerunWorkflow="rerunSelectedRun"
      @exportRunData="exportRunData"
      @exitBuilder="exitBuilder"
      @openWizardDesigner="builderState.openWizardDesigner"
      @updateIsActive="handleUpdateIsActive"
    />

    <!-- Un-forked subscriber base workflow reached directly in editor mode (e.g. a stale link):
         the editor loads but saves would 403 on the edit guard. Surface the Customize path instead.
         Editor-mode only — in history a run is just a run of its targeted version, so it's noise there. -->
    <div
      v-if="isUnforkedSubscriberBase && canvasMode === 'editor'"
      class="alert alert-info d-flex align-items-center justify-content-between gap-3 rounded-0 mb-0 py-2 px-3"
    >
      <span class="small">
        <i class="bi bi-lock me-2"></i>
        Editing requires your own copy. Customize this workflow to create an editable fork.
      </span>
      <button
        class="bb-btn-primary-cta btn-sm flex-shrink-0"
        @click="guardEditEntry()"
      >
        <i class="bi bi-wrench me-1"></i>Customize
      </button>
    </div>

    <!-- Canvas Area -->
    <div ref="canvasWrapperEl" class="builder-canvas-wrapper flex-fill position-relative d-flex">
      <BuilderToolbar
        :canvasMode="canvasMode"
        :viewMode="viewMode"
        :flowDirection="canvasOps.flowDirection.value"
        :hide-controls="canvasMode === 'history' && viewMode === 'canvas'"
        :flat="builderPrefs.flattenStack"
        :has-draft-changes="showApplyAndRun"
        @setCanvasMode="setCanvasMode"
        @toggleViewMode="toggleViewMode"
        @expandAll="handleExpandAll"
        @collapseAll="handleCollapseAll"
        @toggleFlatten="builderPrefs.setFlattenStack(!builderPrefs.flattenStack)"
        @zoomIn="() => zoomIn()"
        @zoomOut="() => zoomOut()"
        @fitView="() => fitView()"
        @applyHierarchicalLayout="handleApplyHierarchicalLayout"
        @toggleFlowDirection="handleToggleFlowDirection"
        @executeWorkflow="handleExecuteWorkflow"
      />
      <HistoryPanel
        scope="workflow"
        :canvasMode="canvasMode"
        :loading="workflowRunsLoading"
        :filtered-run-history="historyMgmt.filteredRunHistory.value"
        :selected-run="historyMgmt.selectedRun.value"
        :search-query="historyFilters.historySearchQuery.value"
        :status-filter="historyFilters.historyStatusFilter.value"
        :status-filters="historyFilters.historyStatusFilters.value"
        :time-shortcut="historyFilters.historyTimeShortcut.value"
        :date-from="historyFilters.historyDateFrom.value"
        :date-to="historyFilters.historyDateTo.value"
        :workflow-filter="historyFilters.historyWorkflowFilter.value"
        :sort-by="historyFilters.historySortBy.value"
        :page-length="historyFilters.historyPageSize.value"
        :total-count="workflowRunsTotal"
        :page="historyFilters.historyPage.value"
        :fetching="workflowRunsFetching"
        :advanced-open="builderPrefs.historyFiltersAdvancedOpen"
        :advanced-filters-dirty="historyFilters.advancedFiltersDirty.value"
        :grouped="builderPrefs.groupChildRuns"
        :show-workflow-filter="false"
        @update-search-query="historyFilters.updateSearchQuery"
        @update-status-filter="historyFilters.updateStatusFilter"
        @toggle-status="historyFilters.toggleStatusFilter"
        @update-time-shortcut="historyFilters.updateTimeShortcut"
        @update-date-range="(from, to) => historyFilters.updateDateRange(from, to)"
        @update-workflow-filter="historyFilters.updateWorkflowFilter"
        @update-sort-by="historyFilters.updateSortBy"
        @update-page-size="historyFilters.updatePageSize"
        @update-page="historyFilters.updatePage"
        @update-advanced-open="builderPrefs.setHistoryFiltersAdvancedOpen"
        @update-grouped="builderPrefs.setGroupChildRuns"
        @reset="historyFilters.resetHistoryFilters"
        @select-run="handleSelectHistoryRun"
      />
    

     
      <!-- Main Canvas Area -->
      <div class="builder-canvas flex-fill position-relative overflow-hidden d-flex flex-column">
        <!-- Run Summary Header (history mode only) -->
        <RunSummaryHeader
          v-if="canvasMode === 'history' && historyMgmt.selectedRun.value"
          :ref="setRunSummaryHeaderEl"
          :run="historyMgmt.selectedRun.value"
          :hide-open-in-builder="true"
          class="builder-run-summary"
        >
          <template #actions>
            <div class="dropdown">
              <button
                  class="bb-btn-secondary-cta btn-sm"
                  type="button"
                  data-bs-toggle="dropdown"
                  title="More options"
              >
                <i class="bi bi-three-dots"></i>
              </button>
              <ul class="dropdown-menu dropdown-menu-end">
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="rerunSelectedRun">
                    <i class="bi bi-arrow-clockwise me-2"></i> Re-run
                  </a>
                </li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="exportRunData">
                    <i class="bi bi-download me-2"></i> Export Run Data
                  </a>
                </li>
                <template v-if="debug.debugToolsVisible">
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <h6 class="dropdown-header text-muted small text-uppercase">Debug</h6>
                  </li>
                  <li>
                    <a
                      class="dropdown-item small text-muted"
                      :class="{ disabled: runSimulator.running.value }"
                      href="#"
                      @click.prevent="runSimulator.start()"
                    >
                      <i class="bi bi-play-circle me-2"></i> Simulate run
                    </a>
                  </li>
                </template>
                <li><hr class="dropdown-divider" /></li>
                <li>
                  <a class="dropdown-item" href="#" @click.prevent="exitBuilder">
                    <i class="bi bi-box-arrow-left me-2"></i> Back to project
                  </a>
                </li>
              </ul>
            </div>
          </template>
        </RunSummaryHeader>
        <!-- Live feed down while the run is still going — say so, else the canvas looks frozen-but-done. -->
        <div
          v-if="liveConnectionLost"
          class="alert alert-warning d-flex align-items-center gap-2 mb-0 rounded-0 py-2"
          role="alert"
        >
          <i class="bi bi-wifi-off"></i>
          <span>Live updates disconnected — this run may have progressed since. Refresh to see the latest.</span>
        </div>
        <div
          ref="canvasInnerEl"
          class="builder-canvas-inner flex-fill position-relative overflow-hidden"
          :class="{ 'editor-stack': canvasMode === 'editor' && viewMode === 'stack' }"
        >
        <!-- Read-only run canvas (history mode). Shared with the project run-history
             tab — owns its own controls, layout, viewport and node-click drill-in.
             Live progress still arrives via selectedRun (patched by useRunProgress),
             which flows through historyNodes/historyEdges into these props. -->
        <RunCanvas
            v-if="viewMode === 'canvas' && canvasMode === 'history' && historyMgmt.selectedRun.value"
            ref="runCanvas"
            :nodes="historyMgmt.historyNodes.value"
            :edges="historyMgmt.historyEdges.value"
            :run-id="historyMgmt.selectedRun.value.id"
        >
          <!-- Toggle rides at the top of RunCanvas's own control cluster (as on the project
               tab), so history+canvas shows one unified cluster instead of two. -->
          <template #controls-top>
            <button
              type="button"
              class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
              title="Switch to List View"
              @click="toggleViewMode"
            >
              <i class="bi bi-list-ul"></i>
            </button>
          </template>
        </RunCanvas>
        <!-- Editor canvas (VueFlow) — editing surface, stays inline in the builder. -->
        <VueFlow
            v-else-if="viewMode === 'canvas'"
            :key="`vueflow-${canvasMode}-${historyMgmt.selectedRun.value?.id ?? ''}-${canvasOps.flowDirection.value}`"
            :class="{ transitioning: isTransitioning }"
            :nodes="enhancedNodes"
            :edges="currentEdges"
            :node-types="nodeTypes"
            :edge-types="edgeTypes"
            :zoom-on-scroll="false"
            :zoom-on-pinch="true"
            :zoom-on-double-click="true"
            @node-click="(event) => handleNodeClick(event.node.id, event.node.data)"
            @node-drag-stop="handleNodeDragStop"
            @connect="onConnect"
            @pane-ready="canvasOps.restoreViewport"
        >
          <Background patternColor="var(--flow-bg-pattern-color)" :gap="20" :size="1.9" />
        </VueFlow>
        <!-- Stacked List View -->
        <StackedWorkflowView
            v-else-if="viewMode === 'stack'"
            ref="stackedWorkflowView"
            :nodes="enhancedNodes"
            :edges="currentEdges"
            :canvas-mode="canvasMode"
            :run-id="canvasMode === 'history' ? historyMgmt.selectedRun.value?.id : undefined"
            :run-workflow-id="canvasMode === 'history' ? historyMgmt.selectedRun.value?.workflowId : undefined"
            :effective-status-by-service="canvasMode === 'history' ? historyMgmt.effectiveStatusByService.value : undefined"
            :run-coloring-active="canvasMode === 'history' ? historyMgmt.runColoringActive.value : undefined"
            :running-nodes="serviceRunningNodes"
            :node-execution-data="serviceLiveRunData"
            :services="resolvedServices"
            :dirty-service-ids="dirtyServiceIds"
            :has-overlay-controls="true"
            :flat="builderPrefs.flattenStack"
            @node-click="(event) => handleNodeClick(event.node.id, event.node.data)"
            @add-service="serviceMgmt.handleAddService"
            @run-service="serviceMgmt.handleRunService"
            @toggle-disabled="nodeOps.handleToggleDisabled"
            @delete-service="nodeOps.handleDeleteService"
            @rename-service="nodeOps.handleRenameService"
            @duplicate-service="nodeOps.handleDuplicateService"
            @edit-parents="nodeOps.handleEditParents"
            @toggle-view-mode="toggleViewMode"
            @expand-all="handleExpandAll"
            @collapse-all="handleCollapseAll"
            @service-update="handleServiceUpdate"
            @service-save="handleServiceSave"
            @service-discard="handleServiceDiscard"
            @toggle-component="handleToggleComponent"
        />

        <!-- Slide-out overlay — renders in both canvas and list view; its own is-visible
             prop controls when it shows. (Previously gated to canvas, so "Add Service" did
             nothing in list view.) -->
        <AddServicePanel
          :is-visible="builderState.showAddServicePanel.value"
          :parent-node-id="builderState.addServiceParentId.value"
          @close="builderState.closeAddServicePanel"
          @add-service="nodeOps.addNewService"
        />

        <!-- Empty-state overlay for history mode with no selected run AND no
             specific run requested in the URL. Avoids covering the canvas when
             the user has deep-linked to a run (which we fetch separately even if
             it's outside the current filter), and avoids flashing during the
             initial fetch. -->
        <div
          v-if="canvasMode === 'history' && !historyMgmt.selectedRun.value && !workflowRunsLoading && !runId"
          class="history-empty-overlay d-flex align-items-start justify-content-center"
        >
          <div class="text-center text-muted py-5 px-4">
            <i class="bi bi-funnel display-4"></i>
            <h3 class="mt-3 text-muted">No matching runs</h3>
            <p class="text-muted mb-3">
              Try widening the time range or clearing other filters.
            </p>
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="historyFilters.resetHistoryFilters"
            >
              <i class="bi bi-arrow-counterclockwise me-1"></i>Reset filters
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
                    

    <!-- Use As New Draft Modal — loads a published version's content into the working draft -->
    <div
      class="modal fade"
      :class="{ show: builderState.showCopyVersionModal.value, 'd-block': builderState.showCopyVersionModal.value }"
      @click.self="builderState.showCopyVersionModal.value = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Use as new draft</h2>
            <button
              type="button"
              class="btn-close"
              @click="builderState.showCopyVersionModal.value = false"
            ></button>
          </div>
          <div class="modal-body">
            <FormAlert :error="restoreWorkflowVersionError" @dismiss="restoreWorkflowVersionReset" />
            <div class="alert alert-warning" role="alert">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>This replaces your current working draft.</strong> Any unpublished changes will be lost.
            </div>
            <p class="mb-0">
              This loads
              <strong>{{ versionMgmt.currentVersion.value?.version }}</strong>'s content into your draft.
              Review it, then publish to make it the live version. Published versions and existing runs
              are unaffected.
            </p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="builderState.showCopyVersionModal.value = false"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bb-btn-primary-cta"
              :disabled="restoreWorkflowVersionPending"
              @click="handleUseAsNewDraft"
            >
              <span v-if="restoreWorkflowVersionPending"
                ><i class="bi bi-hourglass-split me-1"></i>Loading…</span
              >
              <span v-else>Use as new draft</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Publish Version Modal -->
    <div
      class="modal fade"
      :class="{
        show: builderState.showPublishVersionModal.value,
        'd-block': builderState.showPublishVersionModal.value,
      }"
      @click.self="builderState.showPublishVersionModal.value = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">{{ isSubscriberContext ? 'Apply Changes' : 'Publish Version' }}</h2>
            <button
              type="button"
              class="btn-close"
              @click="closePublishModal"
            ></button>
          </div>
          <div class="modal-body">
            <FormAlert :error="publishWorkflowError" @dismiss="publishWorkflowReset" />
            <!-- Subscriber fork: applying just makes the customer's edits live in their own
                 project. No audience, and the version labels are internal/arbitrary, so we skip
                 the version chrome and release notes entirely. -->
            <template v-if="isSubscriberContext">
              <p class="mb-0">
                This makes your draft changes live for this project. New runs will use your updated
                workflow — your existing configuration and connections are unaffected.
              </p>
            </template>

            <!-- Publisher master / DIY: publishing creates a new immutable version. -->
            <template v-else>
              <!-- Current published version -->
              <div class="mb-3 text-muted small">
                <span v-if="versionMgmt.hasPublishedVersions.value">
                  Current published version: <strong>{{ versionMgmt.latestPublishedVersion.value }}</strong>
                </span>
                <span v-else>No published version yet</span>
              </div>

              <!-- Version being published -->
              <div class="mb-4">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-upload text-primary"></i>
                  <span>Publishing as: <strong>{{ versionMgmt.nextPublishVersion.value }}</strong></span>
                </div>
              </div>

              <div class="mb-3">
                <label for="releaseNotes" class="form-label">Release Notes <span class="text-muted">(optional)</span></label>
                <textarea
                  class="form-control"
                  id="releaseNotes"
                  rows="3"
                  v-model="releaseNotes"
                  placeholder="What changed in this version?"
                ></textarea>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="closePublishModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bb-btn-primary-cta"
              :disabled="publishWorkflowPending"
              @click="handlePublishVersion"
            >
              <span v-if="publishWorkflowPending"><i class="bi bi-hourglass-split me-1"></i>{{ isSubscriberContext ? 'Applying…' : 'Publishing…' }}</span>
              <span v-else>{{ isSubscriberContext ? 'Apply Changes' : `Publish ${versionMgmt.nextPublishVersion.value}` }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Discard Changes Modal -->
    <div
      class="modal fade"
      :class="{ show: showDiscardModal, 'd-block': showDiscardModal }"
      @click.self="closeDiscardModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Discard changes</h2>
            <button type="button" class="btn-close" @click="closeDiscardModal"></button>
          </div>
          <div class="modal-body">
            <FormAlert :error="discardWorkflowChangesError" @dismiss="discardWorkflowChangesReset" />
            <div class="alert alert-warning" role="alert">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>This can't be undone.</strong> Your unpublished changes will be permanently lost.
            </div>
            <p class="mb-0">
              This reverts the workflow to its last published version<span
                v-if="versionMgmt.latestPublishedVersion.value"
              >
                (<strong>{{ versionMgmt.latestPublishedVersion.value }}</strong>)</span
              >. Published versions and existing runs are unaffected.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="closeDiscardModal">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="discardWorkflowChangesPending"
              @click="handleDiscardChanges"
            >
              <span v-if="discardWorkflowChangesPending"
                ><i class="bi bi-hourglass-split me-1"></i>Discarding…</span
              >
              <span v-else>Discard changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Import Workflow Confirmation Modal -->
    <div
      class="modal fade"
      :class="{ show: showImportConfirmModal, 'd-block': showImportConfirmModal }"
      @click.self="closeImportConfirmModal"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Import workflow</h2>
            <button type="button" class="btn-close" @click="closeImportConfirmModal"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning" role="alert">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>This can't be undone.</strong> The current contents of this workflow will be
              replaced by the imported file.
            </div>
            <p class="mb-0">
              The workflow's name is kept; its steps, wizard and connection slots are replaced and it
              returns to a draft you can review and publish. Published versions and existing runs are
              unaffected.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="bb-btn-cancel" @click="closeImportConfirmModal">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              :disabled="importWorkflowPending"
              @click="confirmImportWorkflow"
            >
              <span v-if="importWorkflowPending"
                ><i class="bi bi-hourglass-split me-1"></i>Importing…</span
              >
              <span v-else>Replace &amp; import</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Service Details Panel (Editor Mode) -->
    <ServiceDetailsPanel
      v-model="selectedService"
      :is-visible="builderState.showServiceDetails.value"
      :is-dirty="selectedServiceIsDirty"
      :save-error="updateServiceError"
      @dismiss-error="updateServiceReset"
      @close="handleServicePanelClose"
      @run="serviceMgmt.handleServiceRun"
      @save="() => handleServiceSave()"
      @discard="() => handleServiceDiscard()"
      @toggle-component="handleToggleComponent"
    />

    <!-- Filter when, edited from a connector's funnel icon. -->
    <FilterWhenPopover
      v-if="filterPopover && filterPopoverService"
      :expression="filterPopoverService.serviceFilterExpression"
      :scope="filterPopoverScope"
      :anchor="filterPopover.anchor"
      :save-error="filterWhenSaveError"
      :saving="filterWhenSaving"
      @save="saveFilterWhen(filterPopover.serviceId, $event)"
      @close="filterPopover = null"
      @dismiss-error="filterWhenSaveReset"
    />

    <!-- Run Service Details Panel — history + STACK view drill-in. (History + canvas
         view is owned by RunCanvas's own internal panel; the editor VueFlow opens the
         editor's ServiceDetailsPanel instead, so this only ever fires from the stacked
         history list.) -->
    <RunServiceDetailsPanel
      :is-visible="builderState.showRunServiceDetails.value"
      :node-data="builderState.selectedRunServiceData.value"
      :node-id="builderState.selectedRunServiceId.value"
      :run-id="historyMgmt.selectedRun.value?.id ?? ''"
      :services="historyMgmt.selectedRun.value?.services"
      @close="builderState.closeRunServiceDetails"
    />

    <!-- Run Service Modal -->
    <RunServiceModal
      :show="builderState.showRunServiceModal.value"
      :service-data="builderState.runServiceData.value"
      :node-id="builderState.runServiceNodeId.value"
      @hide="serviceMgmt.handleHideRunModal"
      @cancel="serviceMgmt.handleRunModalCancel"
      @execute="serviceMgmt.handleExecuteService"
    />

    <!-- Workflow Wizard Builder -->
    <WorkflowWizardBuilder
      :show="builderState.showWizardDesigner.value"
      :workflow-id="workflow?.id ?? ''"
      :workflow-name="workflow?.name || 'Untitled Workflow'"
      :wizard-definitions="workflow?.wizardDefinitions"
      @close="builderState.showWizardDesigner.value = false"
    />

    <!-- Delete Service Confirmation Modal -->
    <div
      class="modal fade"
      :class="{
        show: builderState.showDeleteServiceModal.value,
        'd-block': builderState.showDeleteServiceModal.value,
      }"
      @click.self="nodeOps.cancelDeleteService"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h2 class="modal-title d-flex align-items-center gap-2">
              <div
                class="d-flex align-items-center justify-content-center bg-danger-subtle rounded-circle"
                style="width: 40px; height: 40px"
              >
                <i class="bi bi-exclamation-triangle text-danger fs-5"></i>
              </div>
              Confirm Delete
            </h2>
            <button
              type="button"
              class="btn-close"
              @click="nodeOps.cancelDeleteService"
            ></button>
          </div>
          <div class="modal-body pt-2">
            <p class="text-muted mb-3">
              Are you sure you want to delete the service
              <strong class="text-body">"{{ builderState.serviceToDelete.value?.name }}"</strong>?
            </p>
            <div
              class="alert alert-warning d-flex align-items-start gap-2"
              role="alert"
            >
              <i class="bi bi-info-circle text-warning mt-1 flex-shrink-0"></i>
              <div class="small">
                <strong>This action cannot be undone.</strong><br />
                All connections to this service will also be removed.
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="nodeOps.cancelDeleteService"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="nodeOps.confirmDeleteService"
            >
              <i class="bi bi-trash me-2"></i>Delete Service
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toggle Service Confirmation Modal -->
    <div
      class="modal fade"
      :class="{
        show: builderState.showToggleServiceModal.value,
        'd-block': builderState.showToggleServiceModal.value,
      }"
      @click.self="nodeOps.cancelToggleService"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h2 class="modal-title d-flex align-items-center gap-2">
              <div
                class="d-flex align-items-center justify-content-center rounded-circle"
                :class="
                  builderState.serviceToToggle.value?.isDisabled
                    ? 'bg-success-subtle'
                    : 'bg-danger-subtle'
                "
                style="width: 40px; height: 40px"
              >
                <i
                  class="bi bi-power fs-5"
                  :class="
                    builderState.serviceToToggle.value?.isDisabled ? 'text-success' : 'text-danger'
                  "
                ></i>
              </div>
              {{ builderState.serviceToToggle.value?.isDisabled ? "Enable" : "Disable" }} Service
            </h2>
            <button
              type="button"
              class="btn-close"
              @click="nodeOps.cancelToggleService"
            ></button>
          </div>
          <div class="modal-body pt-2">
            <p class="text-muted mb-3">
              Are you sure you want to
              {{ builderState.serviceToToggle.value?.isDisabled ? "enable" : "disable" }} the
              service
              <strong class="text-body">"{{ builderState.serviceToToggle.value?.name }}"</strong>?
            </p>
            <div
              class="alert d-flex align-items-start gap-2"
              :class="
                builderState.serviceToToggle.value?.isDisabled ? 'alert-success' : 'alert-danger'
              "
              role="alert"
            >
              <i
                class="bi mt-1 flex-shrink-0"
                :class="
                  builderState.serviceToToggle.value?.isDisabled
                    ? 'bi-info-circle text-success'
                    : 'bi-exclamation-triangle text-danger'
                "
              ></i>
              <div class="small">
                <strong v-if="builderState.serviceToToggle.value?.isDisabled"
                  >Enabling this service</strong
                >
                <strong v-else>Disabling this service</strong>
                will
                {{
                  builderState.serviceToToggle.value?.isDisabled
                    ? "include it in workflow executions"
                    : "skip it during workflow executions"
                }}.
                <br />
                <span v-if="!builderState.serviceToToggle.value?.isDisabled"
                  >Connected services after this one will still execute.</span
                >
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="nodeOps.cancelToggleService"
            >
              Cancel
            </button>
            <button
              type="button"
              :class="
                builderState.serviceToToggle.value?.isDisabled ? 'bb-btn-primary-cta' : 'bb-btn-danger'
              "
              @click="nodeOps.confirmToggleService"
            >
              <i class="bi bi-power me-2"></i>
              {{ builderState.serviceToToggle.value?.isDisabled ? "Enable" : "Disable" }} Service
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Rename Service Modal -->
    <div
      class="modal fade"
      :class="{
        show: builderState.showRenameServiceModal.value,
        'd-block': builderState.showRenameServiceModal.value,
      }"
      @click.self="nodeOps.handleCancelRename"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title d-flex align-items-center gap-2">
              <i
                class="text-primary bi"
                :class="builderState.renameModalMode.value === 'create' ? 'bi-plus-circle' : 'bi-pencil'"
              ></i>
              {{ builderState.renameModalMode.value === 'create' ? 'Name Service' : 'Rename Service' }}
            </h2>
            <button
              type="button"
              class="btn-close"
              @click="nodeOps.handleCancelRename"
            ></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="serviceNameInput" class="form-label"
                >Service Name</label
              >
              <input
                id="serviceNameInput"
                ref="serviceNameInput"
                type="text"
                class="form-control"
                v-model="builderState.renameInputValue.value"
                placeholder="Enter service name"
                @keyup.enter="nodeOps.handleConfirmRename()"
                @keyup.escape="nodeOps.handleCancelRename"
              />
            </div>
            <div class="text-muted small">
              <i class="bi bi-info-circle me-1"></i>
              Press Enter to save, Escape to cancel
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="nodeOps.handleCancelRename"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bb-btn-primary-cta"
              @click="nodeOps.handleConfirmRename()"
            >
              <i class="bi bi-check-lg me-2"></i>
              {{ builderState.renameModalMode.value === 'create' ? 'Save Name' : 'Rename' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Parent Services Modal -->
    <EditParentServicesModal
      :show="builderState.showEditParentsModal.value"
      :service-name="builderState.serviceToEditParents.value?.name ?? ''"
      :service-id="builderState.serviceToEditParents.value?.id ?? ''"
      :services="editParentsServices"
      :current-parent-ids="editParentsCurrentIds"
      @confirm="nodeOps.confirmEditParents"
      @cancel="nodeOps.cancelEditParents"
    />

    <!-- Duplicate Service Confirmation Modal -->
    <div
      class="modal fade"
      :class="{
        show: builderState.showDuplicateServiceModal.value,
        'd-block': builderState.showDuplicateServiceModal.value,
      }"
      @click.self="nodeOps.cancelDuplicateService"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header border-0 pb-0">
            <h2 class="modal-title d-flex align-items-center gap-2">
              <div
                class="d-flex align-items-center justify-content-center bg-primary-subtle rounded-circle"
                style="width: 40px; height: 40px"
              >
                <i class="bi bi-files text-primary fs-5"></i>
              </div>
              Confirm Duplicate
            </h2>
            <button
              type="button"
              class="btn-close"
              @click="nodeOps.cancelDuplicateService"
            ></button>
          </div>
          <div class="modal-body pt-2">
            <p class="mb-3">
              Are you sure you want to duplicate the service
              <strong>{{ builderState.serviceToDuplicate.value?.name }}</strong
              >?
            </p>
            <div
              class="alert alert-info d-flex align-items-start gap-2"
              role="alert"
            >
              <i class="bi bi-info-circle text-info mt-1 flex-shrink-0"></i>
              <div class="small">
                The duplicated service will be created with the same
                configuration as the original, positioned nearby with "(Copy)"
                appended to its name.
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 pt-0">
            <button
              type="button"
              class="bb-btn-cancel"
              @click="nodeOps.cancelDuplicateService"
            >
              Cancel
            </button>
            <button
              type="button"
              class="bb-btn-primary-cta"
              @click="nodeOps.confirmDuplicateService"
            >
              <i class="bi bi-files me-2"></i>Duplicate Service
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div
      v-if="
        builderState.showCopyVersionModal.value ||
        builderState.showPublishVersionModal.value ||
        showDiscardModal ||
        showImportConfirmModal ||
        builderState.showDeleteServiceModal.value ||
        builderState.showToggleServiceModal.value ||
        builderState.showRenameServiceModal.value ||
        builderState.showDuplicateServiceModal.value ||
        builderState.showEditParentsModal.value
      "
      class="modal-backdrop fade show"
      @click="dismissActiveModal"
    ></div>

    <RunReadinessConfirmDialog
      :open="readinessConfirmOpen"
      :issues="readinessIssues"
      :advisories="readinessAdvisories"
      :submitting="readinessSubmitting"
      :project-id="projectId"
      @run-anyway="readinessRunAnyway"
      @cancel="readinessCancel"
    />

    <!-- Apply-first confirm, before the run's own readiness gate. A publish-gate failure renders inline. -->
    <ApplyAndRunConfirmModal
      :show="applyAndRun.confirmOpen.value"
      :workflow-name="workflow?.name"
      :error="publishWorkflowError"
      :applying="applyAndRun.applying.value"
      @confirm="applyAndRun.confirm"
      @cancel="applyAndRun.cancel"
      @dismiss-error="publishWorkflowReset"
    />

    <!-- Re-run modal (history mode) — same modal as the project History tab, but stay-on-success keeps
         the user on the canvas and loads the new run here (see onRerunStarted). -->
    <RunWorkflowModal
      v-model:show="rerunModalOpen"
      :project-id="projectId"
      :workflow-id="rerunWorkflowId"
      :workflow-name="rerunWorkflowName"
      :initial-inputs="rerunInputs"
      stay-on-success
      @ran="onRerunStarted"
    />

    <!-- Confirm-before-forking for an un-forked subscriber base workflow (shared with the Workflows tab). -->
    <CustomizeWorkflowModal
      :show="builderNav.showCustomizeModal.value"
      :workflow-name="builderNav.customizeTarget.value?.name"
      :pending="builderNav.customizeWorkflowPending.value"
      :error-message="builderNav.customizeWorkflowError.value?.message"
      @confirm="builderNav.confirmFork"
      @cancel="builderNav.cancelFork"
    />
  </div>
</template>

<style lang="scss">
// VueFlow library CSS, --flow-bg-pattern-color, and .bb-btn-vueflow-ctrl now live in
// the global stylesheet (src/scss/styles.scss) so the read-only RunCanvas gets them on
// the project run-history tab too, not only while the builder is mounted.
@import 'bootstrap/scss/bootstrap';

// Shared hover behaviour — included into any interactive builder surface
@mixin bb-builder-hover {
  //border-color: var(--bs-tertiary-color);
  //box-shadow: 0 0.25em 0.75em rgba(0, 0, 0, 0.15);
  transform: translateY(-0.125em); // lift effect — transform doesn't affect layout flow so siblings don't shift
}

// Flexbox min-height reset down the canvas chain. Each of these is a flex-fill child of a
// flex parent, and the default `min-height: auto` lets a flex item grow to its content rather
// than shrink to its parent — so the VueFlow pane measured a height taller than the visible
// canvas, and fitView/auto-layout centred into that oversized box, sinking the graph to the
// bottom. `min-height: 0` bounds the pane to what's actually on screen so fitView centres true.
.builder-canvas-wrapper,
.builder-canvas,
.builder-canvas-inner {
  min-height: 0;
}

// Interactive panel surfaces — canvas nodes, stacked cards
.builder-canvas-wrapper .builder-canvas .bb-builder-panel {
  cursor: pointer;
  //transition: all 0.2s ease;

  &:hover {
    @include bb-builder-hover;
  }
}

// .bb-btn-vueflow-ctrl moved to the global stylesheet (shared with RunCanvas).
</style>

<style scoped lang="scss">
.run-history-item {
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
}

//.run-history-item:hover {
//  background-color: var(--bs-gray-100) !important;
//}



/* Builder Container Height */
//option legacy
//.builder-container {
//  height: calc(100vh - 80px); //Account for header, navigation, and margins 
//min-height: 600px;
//}


//option1 
//.builder-container {
//  height: calc(100vh - 80px);
//}
//
//// Add this to make flex children actually fill height                                                                                                                        
//.builder-container > .flex-fill {
//  min-height: 0;  // Override Bootstrap's min-height: auto                                                                                                                    
//  height: 0;      // Force flex-grow to determine height                                                                                                                      
//}


.builder-container {
  height: 100%; // Fill parent (which already has calc(100vh - 80px) in App.vue)
}

// The grey tertiary-bg surface the project + global run-history tabs use (.history-tab-detail), so
// the builder's editor and history canvases match all three run-history views in both themes — the
// VueFlow dot-grid and the transparent RunCanvas / StackedWorkflowView all sit on grey rather than the
// old near-black. Light mode already inherited this grey from the body; dark mode needs it stated,
// since Bootstrap's dark body-bg is darker than tertiary-bg.
[data-bs-theme="dark"] .builder-container {
  background-color: var(--bs-tertiary-bg);
}

// Watermark on the canvas column (which wraps both the run banner and the canvas pane), so the grey
// runs full-height behind the header — matching .history-tab-detail — with no dark stripe at the top.
.builder-canvas {
  background: var(--bs-tertiary-bg) url(/images/watermark.png) bottom right no-repeat;
}

// Editor + stack has no RunSummaryHeader above the list (unlike history/project/global, whose header
// margin gives the first card breathing room), so the stack butts up against the canvas top. Add the
// matching gap here. The editor+stack toggle button is bumped down by the same amount in BuilderToolbar
// (.stack-mode:not(.history-mode) .zoom-controls) so the two stay aligned.
.builder-canvas-inner.editor-stack {
  padding-top: 0.75rem;
}



//[data-bs-theme="dark"] .btn-outline-primary {
//  border-color: #4b5563;
//  color: #d1d5db;
//  background-color: #374151;
//}
//
//[data-bs-theme="dark"] .btn-outline-primary:hover {
//  background-color: #4b5563;
//  border-color: #6b7280;
//  color: #f3f4f6;
//}

.cursor-pointer {
  cursor: pointer;
}

.dropdown-submenu {
  position: relative;
}

.testing-context-submenu {
  position: absolute;
  top: 0;
  right: 100%;
  margin-right: 2px;
  min-width: 250px;
  ////z-index: 1010 !important;
}

/* VueFlow uses built-in edge animations via the animated property */

.history-empty-overlay {
  position: absolute;
  inset: 0;
  background: var(--bs-body-bg);
  // BuilderToolbar sits at z-index: 5 with its mode-toggle and zoom controls.
  // Stay below it so those overlay controls remain visible/clickable.
  z-index: 1;
}

/* Reduce flash during viewport transitions */
.transitioning {
  opacity: 0.1;
  transition: opacity 0.05s ease-out;
}


/* Builder-specific component styles */
.builder-dropdown-wide {
  min-width: 350px;
}

.builder-workflow-dropdown {
  min-width: 300px;
}


.builder-dropdown-z {
  position: relative;
  //z-index: 15;
}

.builder-version-dropdown {
  //z-index: 30 !important;
}

.service-details-width {
  width: 120px;
}

.project-picker-modal {
  background-color: rgba(0, 0, 0, 0.5);
  //z-index: 1070;
}

@media (max-width: 768px) {
  .border-bottom .d-flex.justify-content-between {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 1rem;
  }

  .border-bottom .d-flex.justify-content-between nav {
    width: 100%;
  }
}

/* Builder page body styles */
@media (max-width: 768px) {
  :deep(html body.builder-page) {
    height: 100vh;
    max-height: 100vh;
    overflow: hidden;
  }
}




</style>



<!-- Project Picker Modal -->
<!--  <div-->
<!--    class="modal fade project-picker-modal"-->
<!--    :class="{ 'show d-block': showProjectPicker }"-->
<!--    tabindex="-1"-->
<!--    v-if="showProjectPicker"-->
<!--  >-->
<!--    <div class="modal-dialog modal-dialog-centered">-->
<!--      <div class="modal-content">-->
<!--        <div class="modal-header">-->
<!--          <h2 class="modal-title">Select a Project</h2>-->
<!--          <button-->
<!--            type="button"-->
<!--            class="btn-close"-->
<!--            @click="showProjectPicker = false"-->
<!--          ></button>-->
<!--        </div>-->
<!--        <div class="modal-body">-->
<!--          <p class="text-muted mb-3">-->
<!--            Choose a project to open in the builder:-->
<!--          </p>-->
<!--          <div v-if="projectsLoading" class="text-center py-4">-->
<!--            <div class="spinner-border text-primary" role="status">-->
<!--              <span class="visually-hidden">Loading...</span>-->
<!--            </div>-->
<!--          </div>-->
<!--          <div v-else-if="projects.length === 0" class="text-center py-4">-->
<!--            <p class="text-muted">-->
<!--              No projects found.-->
<!--              <a href="/projects" class="text-decoration-none"-->
<!--                >Create a project first</a-->
<!--              >.-->
<!--            </p>-->
<!--          </div>-->
<!--          <div v-else class="list-group">-->
<!--            <button-->
<!--              v-for="project in projects"-->
<!--              :key="project.id"-->
<!--              @click="selectProject(project)"-->
<!--              class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"-->
<!--            >-->
<!--              <div>-->
<!--                <h6 class="mb-1">{{ project.name }}</h6>-->
<!--                <small class="text-muted">{{-->
<!--                  project.productId || "No product assigned"-->
<!--                }}</small>-->
<!--              </div>-->
<!--              <i class="bi bi-arrow-right"></i>-->
<!--            </button>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
<!--    </div>-->
<!--  </div>-->
